import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { vehiclesAPI, sitesAPI, workstreamAPI, clientsAPI, vehicleStatusLogAPI, adminUtilityAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  formatDateBritish,
  formatDateTimeBritish,
  formatLondonDate,
  formatLondonDateTime,
  formatLondonTime,
  parseApiTimestamp,
} from '../utils/dateUtils';
import './VehicleList.css';
import additionIcon from '../assets/icons/addition.png';
import shieldIcon from '../assets/shield-icon.png';
import plainVanImg from '../assets/plain-van.png';

interface Vehicle {
  id: number;
  registration: string;
  make: string;
  model: string;
  vehicleType?: string;
  weightClass?: string;
  grossVehicleWeightKg?: number;
  year: string;
  clientId: number;
  client?: { id: number; companyName: string; city?: string; postcode?: string };
  siteId?: number;
  site?: { id: number; name: string };
  vehicleValue?: number;
  coverStatus?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
  isActive: boolean;
  hasPendingReinstatement?: boolean;
  hasModifications?: boolean;
  modificationDetails?: string;
  registeredOwnerIsBusiness?: boolean | null;
  registeredOwnerBusinessName?: string;
  registeredOwnerName?: string;
  registeredOwnerAddress?: string;
  registeredKeeperIsBusiness?: boolean | null;
  registeredKeeperBusinessName?: string;
  registeredKeeperName?: string;
  registeredKeeperAddress?: string;
  tempCoverInfo?: { coverStartDate: string; coverEndDate: string } | null;
  inceptionDate?: string;
  coverEffectiveDate?: string; // Date/time cover was confirmed (instant cover or admin approval)
  coverEndDate?: string; // Auto-calculated: inception + 365 days
  daysRemaining?: number; // Auto-calculated: days until cover ends
}

interface Site {
  id: number;
  name: string;
  clientId: number;
}

// ─── Vehicle value helpers ──────────────────────────────────────────────────
const _vvOnes = ['','one','two','three','four','five','six','seven','eight','nine',
  'ten','eleven','twelve','thirteen','fourteen','fifteen','sixteen',
  'seventeen','eighteen','nineteen'];
const _vvTens = ['','','twenty','thirty','forty','fifty','sixty','seventy','eighty','ninety'];

function numberToWords(n: number): string {
  if (n === 0) return 'zero';
  if (n >= 1_000_000) {
    const m = Math.floor(n / 1_000_000), rem = n % 1_000_000;
    return numberToWords(m) + ' million' + (rem ? ' ' + numberToWords(rem) : '');
  }
  if (n >= 1_000) {
    const t = Math.floor(n / 1_000), rem = n % 1_000;
    return numberToWords(t) + ' thousand' + (rem ? ' ' + numberToWords(rem) : '');
  }
  if (n >= 100) {
    const h = Math.floor(n / 100), rem = n % 100;
    return _vvOnes[h] + ' hundred' + (rem ? ' and ' + numberToWords(rem) : '');
  }
  if (n >= 20) {
    const t = Math.floor(n / 10), r = n % 10;
    return _vvTens[t] + (r ? '-' + _vvOnes[r] : '');
  }
  return _vvOnes[n];
}

function vehicleValuePreview(raw: string): { monetary: string; words: string } | null {
  const num = parseInt(raw.replace(/[^0-9]/g, ''), 10);
  if (!raw || isNaN(num) || num <= 0) return null;
  const w = numberToWords(num);
  return {
    monetary: '\u00a3' + num.toLocaleString('en-GB'),
    words: w.charAt(0).toUpperCase() + w.slice(1) + ' pounds',
  };
}

// ─────────────────────────────────────────────────────────────────────────────

// ─── InfoTooltip — click-to-toggle, fixed-positioned to avoid modal clipping ──
const InfoTooltip: React.FC<{ label: string; content: React.ReactNode }> = ({ label, content }) => {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const tooltipWidth = Math.min(540, window.innerWidth - 24);
      const estimatedHeight = 320;
      const left = Math.max(8, Math.min(rect.left - 60, window.innerWidth - tooltipWidth - 8));
      const rawTop = rect.bottom + 6;
      const top = Math.min(rawTop, window.innerHeight - estimatedHeight - 8);
      setPos({ top, left });
    }
    setOpen(v => !v);
  };

  return (
    <span style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: 5 }}>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        title={label}
        style={{
          background: '#eff6ff', border: '1.5px solid #3b82f6', borderRadius: '50%',
          cursor: 'pointer', color: '#1d4ed8', fontWeight: 700, fontSize: 11,
          width: 18, height: 18, lineHeight: 1, padding: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >?</button>
      {open && pos && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 9997 }} />
          <div style={{
            position: 'fixed', top: pos.top, left: pos.left, zIndex: 9998,
            width: Math.min(540, window.innerWidth - 24),
            background: '#1e293b', color: '#f1f5f9', borderRadius: 8,
            padding: '12px 14px 12px 14px', fontSize: 13, lineHeight: 1.65,
            boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
          } as React.CSSProperties}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 7 }}>
              <div style={{ fontWeight: 700, color: '#93c5fd', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: 'transparent', border: 'none', color: '#94a3b8',
                  cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 0 0 8px',
                  flexShrink: 0,
                }}
                title="Close"
              >✕</button>
            </div>
            {content}
          </div>
        </>
      )}
    </span>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const VehicleList: React.FC = () => {
  const { isAdmin: isAdminRole, isMTA, user, loading: authLoading } = useAuth();
  const isAdmin = isAdminRole || isMTA; // MTA gets full admin vehicle list view with client selector
  const isSuperAdmin = user?.role === 'SuperAdmin';
  const isOnboarder = user?.role === 'Onboarder';
  const canBackdateRemoval = user?.role === 'Admin'
    || user?.role === 'SuperUser'
    || user?.role === 'SuperAdmin'
    || user?.role === 'MTA'
    || user?.role === 'Onboarder'
    || user?.role === 'FleetManagerAdmin';
  const canBulkUpload = isSuperAdmin || isOnboarder;
  const canSuppressVehicleNotifications = isSuperAdmin || isOnboarder;
  // Site IDs assigned to this user — Client-role users only see vehicles at their assigned sites.
  const userSiteIds = (!isAdmin && user?.role === 'Client') ? (user?.siteIds ?? []) : [];
  const [clientVehicleImage, setClientVehicleImage] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const clientIdFilter = searchParams.get('clientId');
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState<any>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyNote, setHistoryNote] = useState('');
  const [savingHistoryNote, setSavingHistoryNote] = useState(false);
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [suppressVehicleNotifications, setSuppressVehicleNotifications] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number>(0);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [initialOnboardingUpload, setInitialOnboardingUpload] = useState(false);
  const [searchFilter, setSearchFilter] = useState<string>('');
  // Default to 0 (no selection) when no clientId URL param — prevents admin from loading all vehicles.
  // -1 was the old default but it bypassed the "select a client first" guard (which checks for === 0).
  const [viewClientId, setViewClientId] = useState<number>(parseInt(clientIdFilter || '0'));
  const [bulkInceptionDate, setBulkInceptionDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [uploadMode, setUploadMode] = useState<'single-date' | 'custom-dates'>('single-date');
  const [formData, setFormData] = useState({
    registration: '',
    make: '',
    model: '',
    vehicleType: '',
    weightClass: '',
    grossVehicleWeightKg: undefined as number | undefined,
    year: new Date().getFullYear().toString(),
    clientId: 0, // Backend will assign correct clientId for client users
    siteId: undefined as number | undefined,
    vehicleValue: '',
    hasModifications: '',
    modificationDetails: '',
    inceptionDate: new Date().toISOString().split('T')[0],
    // Registered Owner
    registeredOwnerIsBusiness: '' as '' | 'yes' | 'no',
    registeredOwnerBusinessName: '',
    registeredOwnerName: '',
    registeredOwnerAddress: '',
    // Registered Keeper
    registeredKeeperIsBusiness: '' as '' | 'yes' | 'no',
    registeredKeeperBusinessName: '',
    registeredKeeperName: '',
    registeredKeeperAddress: '',
  });
  const [showModifications, setShowModifications] = useState(false);
  
  // New modal states
  const [showTempCoverModal, setShowTempCoverModal] = useState(false);
  const [showChangeVehicleModal, setShowChangeVehicleModal] = useState(false);
  const [showRemoveVehicleModal, setShowRemoveVehicleModal] = useState(false);
  
  // Bulk date amendment state
  const [showBulkDateModal, setShowBulkDateModal] = useState(false);
  const [selectedVehicles, setSelectedVehicles] = useState<Set<number>>(new Set());
  const [bulkAmendDate, setBulkAmendDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [bulkAmendSubmitting, setBulkAmendSubmitting] = useState(false);

  // Inline status log state (embedded in Add Vehicle + Add Temp Vehicle forms — required)
  const [addVehicleStatusRemoved, setAddVehicleStatusRemoved] = useState<string>('');
  const [addVehicleStatusVehicleId, setAddVehicleStatusVehicleId] = useState<string>('');
  const [tempVehicleStatusRemoved, setTempVehicleStatusRemoved] = useState<string>('');
  const [tempVehicleStatusVehicleId, setTempVehicleStatusVehicleId] = useState<string>('');
  // datetime state for temp cover and add-vehicle removal date
  const [tempCoverStartDT, setTempCoverStartDT] = useState<string>('');
  const [tempCoverEndDT, setTempCoverEndDT] = useState<string>('');
  const [addVehicleRemovalDT, setAddVehicleRemovalDT] = useState<string>('');

  // Additional controlled states for temp cover (needed for summary step)
  const [tempHasModifications, setTempHasModifications] = useState<string>('');
  const [tempModificationDetails, setTempModificationDetails] = useState<string>('');
  const [tempSiteIdRaw, setTempSiteIdRaw] = useState<string>('');
  const [tempGrossVehicleWeightKg, setTempGrossVehicleWeightKg] = useState<number | undefined>();

  // Summary step states
  const [showTempSummary, setShowTempSummary] = useState(false);
  const [tempTcConfirmed, setTempTcConfirmed] = useState(false);
  const [showRemovalSummary, setShowRemovalSummary] = useState(false);
  const [removalVehicleId, setRemovalVehicleId] = useState<string>('');
  const [removalCoverEndDate, setRemovalCoverEndDate] = useState<string>('');
  const [removalTcConfirmed, setRemovalTcConfirmed] = useState(false);

  // Vehicle value live preview state for uncontrolled forms
  const [tempVehicleValue, setTempVehicleValue] = useState<string>('');
  const [changeVehicleValue, setChangeVehicleValue] = useState<string>('');

  // Form submission loading states — prevent double-clicks and show vehicle throbber
  const [submitting, setSubmitting] = useState(false);
  const [tempSubmitting, setTempSubmitting] = useState(false);
  const [changeSubmitting, setChangeSubmitting] = useState(false);

  // Per-client vehicle category feature flag
  const [clientRequiresVehicleCategory, setClientRequiresVehicleCategory] = useState(false);

  // Client details used to auto-fill Registered Owner/Keeper when 'Yes' is selected
  const [clientDetails, setClientDetails] = useState<{ name: string; address: string; mtaValueThreshold?: number | null } | null>(null);

  // Policy start/end dates from the client's cover settings — used in vehicle list columns
  const [clientPolicyEndDate, setClientPolicyEndDate]   = useState<string | null>(null);
  const [clientPolicyStartDate, setClientPolicyStartDate] = useState<string | null>(null);

  // Vehicle list filter / sort state (pure client-side — no extra API calls)
  const [filterCoverStatus, setFilterCoverStatus] = useState<string>('');
  const [filterSiteId, setFilterSiteId]           = useState<string>('');
  const [filterCoverType, setFilterCoverType]     = useState<string>('');
  const [sortRegAZ, setSortRegAZ]                 = useState<boolean>(false);

  const buildClientAddress = (c: any): string => [
    c.addressLine1, c.addressLine2, c.city, c.county, c.postcode
  ].filter(Boolean).join('\n');
  const [vehicleCategory, setVehicleCategory]             = useState('');
  const [tempVehicleCategory, setTempVehicleCategory]     = useState('');
  const [businessIsOwnerAndKeeper, setBusinessIsOwnerAndKeeper] = useState<'' | 'yes' | 'lease' | 'other'>('');
  const [businessIsOwnerOtherDetails, setBusinessIsOwnerOtherDetails] = useState('');
  const [tempBusinessIsOwnerAndKeeper, setTempBusinessIsOwnerAndKeeper] = useState<'' | 'yes' | 'lease' | 'other'>('');
  const [tempBusinessIsOwnerOtherDetails, setTempBusinessIsOwnerOtherDetails] = useState('');
  // Perm vehicle T&C / confirmation summary step
  const [showPermVehicleSummary, setShowPermVehicleSummary] = useState(false);
  const [permTcConfirmed, setPermTcConfirmed] = useState(false);

  // Change of Site state
  const [showChangeSiteModal, setShowChangeSiteModal]     = useState(false);
  const [changeSiteVehicleId, setChangeSiteVehicleId]     = useState<number>(0);
  const [changeSiteNewSiteId, setChangeSiteNewSiteId]     = useState<number>(0);
  const [changeSiteNotes, setChangeSiteNotes]             = useState('');
  const [changeSiteSubmitting, setChangeSiteSubmitting]   = useState(false);
  const [changeSiteVehicleSearch, setChangeSiteVehicleSearch] = useState('');
  const [showChangeSiteConfirm, setShowChangeSiteConfirm] = useState(false);

  // VRM lookup
  const [vrmLookupLoading, setVrmLookupLoading] = useState(false);
  const [vrmLookupResult, setVrmLookupResult] = useState<'none'|'found'|'confirmed'|'rejected'|'notfound'>('none');
  const [tempVrmLookupResult, setTempVrmLookupResult] = useState<'none'|'found'|'confirmed'|'rejected'|'notfound'>('none');
  const [changeVrmLookupResult, setChangeVrmLookupResult] = useState<'none'|'found'|'confirmed'|'rejected'|'notfound'>('none');

  // Controlled fields for Temp Cover form (populated by VRM lookup)
  const [tempFormReg, setTempFormReg] = useState('');
  const [tempFormMake, setTempFormMake] = useState('');
  const [tempFormModel, setTempFormModel] = useState('');
  const [tempFormYear, setTempFormYear] = useState('');
  const [tempFormVehicleType, setTempFormVehicleType] = useState('');
  const [tempFormWeightClass, setTempFormWeightClass] = useState('');

  // Controlled fields for Change of Vehicle form (populated by VRM lookup)
  const [changeFormReg, setChangeFormReg] = useState('');
  const [changeFormMake, setChangeFormMake] = useState('');
  const [changeFormModel, setChangeFormModel] = useState('');
  const [changeFormYear, setChangeFormYear] = useState('');
  const [changeFormVehicleType, setChangeFormVehicleType] = useState('');
  const [changeFormWeightClass, setChangeFormWeightClass] = useState('');

  // Sites, clients, and per-client settings load once on mount
  useEffect(() => {
    loadSites();
    if (isAdmin) {
      loadClients();
    } else if (user?.clientId) {
      // Load client feature flags AND details for auto-fill
      clientsAPI.getById(user.clientId)
        .then(r => {
          setClientRequiresVehicleCategory(r.data.requiresVehicleCategory || false);
          setClientDetails({ name: r.data.companyName, address: buildClientAddress(r.data), mtaValueThreshold: r.data.mtaValueThreshold ?? null });
          setClientPolicyEndDate(r.data.policyEndDate ?? null);
          setClientPolicyStartDate(r.data.policyStartDate ?? null);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When an admin selects a different client, reload their details for auto-fill
  useEffect(() => {
    if (!isAdmin || viewClientId <= 0) { setClientDetails(null); return; }
    clientsAPI.getById(viewClientId)
      .then(r => {
        setClientDetails({ name: r.data.companyName, address: buildClientAddress(r.data), mtaValueThreshold: r.data.mtaValueThreshold ?? null });
        setClientPolicyEndDate(r.data.policyEndDate ?? null);
        setClientPolicyStartDate(r.data.policyStartDate ?? null);
      })
      .catch(() => { setClientDetails(null); setClientPolicyEndDate(null); setClientPolicyStartDate(null); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewClientId]);

  // Vehicles reload when the selected client changes, or when auth finishes loading.
  // user is in the dep array so that the effect re-runs once the auth context
  // resolves — preventing the race condition where loadVehicles fires before
  // user.clientId is populated (causing client users to always see an empty list).
  useEffect(() => {
    if (authLoading) return; // auth not ready yet — wait
    loadVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIdFilter, viewClientId, user, authLoading]);

  const loadVehicles = async () => {
    try {
      // Admins must select a client before vehicles load (viewClientId <= 0 = no selection)
      if (isAdmin && viewClientId <= 0) {
        setVehicles([]);
        setLoading(false);
        return;
      }

      // Always use the by-client endpoint — it uses a Select projection (no Include joins),
      // runs fast, and now returns the full field set including Site, TempCoverInfo, cover dates.
      // Admins: scope to selected client. Clients: scope to their own clientId.
      const clientId = isAdmin ? viewClientId : user?.clientId;
      if (!clientId || clientId <= 0) {
        setVehicles([]);
        setLoading(false);
        return;
      }
      const response = await vehiclesAPI.getByClient(clientId);
      // Filter out inactive vehicles from display — only On Cover and Cover Pending shown
      let activeVehicles = response.data.filter((vehicle: Vehicle) =>
        vehicle.isActive !== false && vehicle.coverStatus !== 'Inactive'
      );
      
      // Sort by newest first (createdAt descending)
      activeVehicles.sort((a: Vehicle, b: Vehicle) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      
      setVehicles(activeVehicles);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddHistoryNote = async () => {
    const note = historyNote.trim();
    if (!note || !historyData?.vehicle?.id) return;

    setSavingHistoryNote(true);
    try {
      await vehiclesAPI.addHistoryNote(historyData.vehicle.id, note);
      setHistoryNote('');
      await handleViewHistory(historyData.vehicle.id);
    } catch (error: any) {
      console.error('Error adding vehicle history note:', error);
      alert(error.response?.data?.message || 'Failed to add vehicle history note.');
    } finally {
      setSavingHistoryNote(false);
    }
  };

  const loadSites = async () => {
    try {
      const response = await sitesAPI.getAll();
      setSites(response.data.filter((s: any) => s.isActive !== false));
    } catch (error) {
      console.error('Error loading sites:', error);
    }
  };

  const loadClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      // Backend already filters for active clients
      setClients(response.data);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const withDates = uploadMode === 'custom-dates';
      const response = await vehiclesAPI.downloadCsvTemplate(withDates);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = withDates ? 'vehicle_upload_template_with_dates.csv' : 'vehicle_upload_template.csv';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Failed to download template');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setCsvFile(e.target.files[0]);
      setUploadResult(null);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      alert('Please select a CSV file');
      return;
    }
    if (selectedClientId === 0) {
      alert('Please select a client');
      return;
    }

    try {
      // For single-date mode, pass the bulk inception date; for custom-dates mode, pass undefined
      const inceptionDate = uploadMode === 'single-date' ? bulkInceptionDate : undefined;
      const response = await vehiclesAPI.uploadCsv(csvFile, selectedClientId, inceptionDate, initialOnboardingUpload);
      setUploadResult(response.data);
      if (response.data.successCount > 0) {
        loadVehicles();
      }
    } catch (error: any) {
      console.error('Error uploading CSV:', error);
      const errorMsg = error.response?.data?.error 
        ? `${error.response.data.message}: ${error.response.data.error}`
        : error.response?.data?.message || 'Failed to upload CSV file';
      alert(errorMsg);
      console.error('Full error details:', error.response?.data);
    }
  };

  const handleUndoUpload = async () => {
    if (!uploadResult?.uploadBatchId) {
      alert('No upload to undo');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to undo this upload? This will remove ${uploadResult.successCount} vehicle(s) from the database.`
    );

    if (!confirmed) return;

    try {
      const response = await vehiclesAPI.undoUpload(uploadResult.uploadBatchId);
      alert(response.data.message);
      setUploadResult(null);
      setCsvFile(null);
      loadVehicles();
    } catch (error: any) {
      console.error('Error undoing upload:', error);
      alert(error.response?.data?.message || 'Failed to undo upload');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate before showing the loading overlay
    if (!editingVehicle) {
      if (!addVehicleStatusRemoved) {
        alert('Please select the vehicle status before submitting.');
        return;
      }
      if (addVehicleStatusRemoved === 'yes' && !addVehicleStatusVehicleId) {
        alert('Please select the vehicle being removed.');
        return;
      }
    }

// Validate modification details are provided when modifications are declared
    if (formData.hasModifications === 'yes' && !formData.modificationDetails.trim()) {
      alert('Please describe the vehicle modifications before submitting.');
      return;
    }

    // Cover start date is mandatory for new permanent vehicles (field can be unmounted on T&C step)
    if (!editingVehicle && !formData.inceptionDate?.trim()) {
      alert('Please enter a Cover Start Date before submitting.');
      setShowPermVehicleSummary(false);
      return;
    }

    // For new perm vehicles added by client users, show summary + T&C step first
    if (!editingVehicle && !isAdmin && !showPermVehicleSummary) {
      setShowPermVehicleSummary(true);
      return;
    }

    setSubmitting(true);
    try {
      // For admin users, clientId must come from the selected client (viewClientId)
      const ownerPayload = {
        registeredOwnerIsBusiness:   formData.registeredOwnerIsBusiness === '' ? null : formData.registeredOwnerIsBusiness === 'yes',
        registeredOwnerBusinessName: formData.registeredOwnerIsBusiness === 'yes' ? (formData.registeredOwnerBusinessName || null) : null,
        registeredOwnerName:         formData.registeredOwnerIsBusiness === 'no'  ? (formData.registeredOwnerName    || null) : null,
        registeredOwnerAddress:      formData.registeredOwnerIsBusiness === 'no'  ? (formData.registeredOwnerAddress || null) : null,
        registeredKeeperIsBusiness:   formData.registeredKeeperIsBusiness === '' ? null : formData.registeredKeeperIsBusiness === 'yes',
        registeredKeeperBusinessName: formData.registeredKeeperIsBusiness === 'yes' ? (formData.registeredKeeperBusinessName || null) : null,
        registeredKeeperName:         formData.registeredKeeperIsBusiness === 'no'  ? (formData.registeredKeeperName    || null) : null,
        registeredKeeperAddress:      formData.registeredKeeperIsBusiness === 'no'  ? (formData.registeredKeeperAddress || null) : null,
      };

const submitData = {
        ...formData,
        clientId: isAdmin && viewClientId > 0 ? viewClientId : formData.clientId,
        inceptionDate: formData.inceptionDate?.trim() || null,
        hasModifications: formData.hasModifications === 'yes',
        modificationDetails: formData.hasModifications === 'yes' ? formData.modificationDetails : null,
        vehicleCategory: clientRequiresVehicleCategory ? (vehicleCategory || null) : null,
        suppressNotifications: canSuppressVehicleNotifications && suppressVehicleNotifications,
        ...ownerPayload,
      };

      if (editingVehicle) {
        const updateData = {
          id: editingVehicle.id,
          registration: formData.registration,
          make: formData.make,
          model: formData.model,
          vehicleType: formData.vehicleType,
          weightClass: formData.weightClass,
          grossVehicleWeightKg: formData.grossVehicleWeightKg ?? null,
          year: formData.year,
          clientId: isAdmin && viewClientId > 0 ? viewClientId : formData.clientId,
          siteId: formData.siteId || null,
          vehicleValue: formData.vehicleValue,
          hasModifications: formData.hasModifications === 'yes',
          modificationDetails: formData.hasModifications === 'yes' ? formData.modificationDetails : null,
          inceptionDate: formData.inceptionDate || null,
          coverStatus: editingVehicle.coverStatus,
          createdAt: editingVehicle.createdAt,
          updatedAt: editingVehicle.updatedAt,
          createdBy: editingVehicle.createdBy,
          updatedBy: editingVehicle.updatedBy,
          isActive: editingVehicle.isActive,
          ...ownerPayload,
        };
        const updateResponse = await vehiclesAPI.update(updateData.id, updateData);
        setVehicles(prev => prev.map(v =>
          v.id === updateData.id ? { ...v, ...(updateResponse.data || updateData) } : v
        ));
        resetForm();
      } else {
        const newVehicleResponse = await vehiclesAPI.create(submitData);
        const removedVehicle = addVehicleStatusRemoved === 'yes'
          ? vehicles.find(v => v.id === parseInt(addVehicleStatusVehicleId))
          : null;
        try {
          await vehicleStatusLogAPI.submit({
            vehicleId: removedVehicle ? removedVehicle.id : newVehicleResponse.data.id,
            registration: removedVehicle ? removedVehicle.registration : formData.registration,
            vehicleRemoved: addVehicleStatusRemoved === 'yes',
            reason: null,
          });
        } catch { /* non-critical */ }
        if (removedVehicle) {
          try {
              await workstreamAPI.requestRemoval(
              removedVehicle.id,
              undefined,
              addVehicleRemovalDT || formData.inceptionDate
            );
          } catch { /* non-critical */ }
          try {
            await vehiclesAPI.update(removedVehicle.id, {
              ...removedVehicle,
              isActive: false,
              coverStatus: 'Inactive',
            });
          } catch { /* non-critical */ }
        }
        loadVehicles();
        resetForm();
      }
    } catch (error: any) {
      console.error('Error saving vehicle:', error);
      const errorMsg = error.response?.data?.errors
        ? Object.entries(error.response.data.errors).map(([field, msgs]: [string, any]) =>
            `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`
          ).join('\n')
        : error.response?.data?.message || 'Failed to save vehicle';
      alert(`Failed to save vehicle:\n${errorMsg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    const hasModsValue = vehicle.hasModifications ? 'yes' : 'no';
    const ownerIsBiz  = vehicle.registeredOwnerIsBusiness;
    const keeperIsBiz = vehicle.registeredKeeperIsBusiness;
    setFormData({
      registration: vehicle.registration,
      make: vehicle.make,
      model: vehicle.model,
      vehicleType: vehicle.vehicleType || '',
      weightClass: vehicle.weightClass || '',
      grossVehicleWeightKg: vehicle.grossVehicleWeightKg,
      year: vehicle.year,
      clientId: vehicle.clientId,
      siteId: vehicle.siteId,
      vehicleValue: vehicle.vehicleValue?.toString() || '',
      hasModifications: hasModsValue,
      modificationDetails: vehicle.modificationDetails || '',
      inceptionDate: vehicle.inceptionDate ? new Date(vehicle.inceptionDate).toISOString().split('T')[0] : '',
      registeredOwnerIsBusiness:   ownerIsBiz  == null ? '' : ownerIsBiz  ? 'yes' : 'no',
      registeredOwnerBusinessName: vehicle.registeredOwnerBusinessName || '',
      registeredOwnerName:         vehicle.registeredOwnerName         || '',
      registeredOwnerAddress:      vehicle.registeredOwnerAddress      || '',
      registeredKeeperIsBusiness:   keeperIsBiz == null ? '' : keeperIsBiz ? 'yes' : 'no',
      registeredKeeperBusinessName: vehicle.registeredKeeperBusinessName || '',
      registeredKeeperName:         vehicle.registeredKeeperName         || '',
      registeredKeeperAddress:      vehicle.registeredKeeperAddress      || '',
    });
    setShowModifications(vehicle.hasModifications || false);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this vehicle? It will be removed from view but kept in the system.')) {
      return;
    }

    try {
      await vehiclesAPI.delete(id);
      // Remove from local state immediately for better UX
      setVehicles(vehicles.filter(v => v.id !== id));
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      alert('Failed to delete vehicle');
      loadVehicles(); // Reload if there was an error
    }
  };

  const handleRequestReinstatement = async (vehicleId: number, registration: string) => {
    const notes = prompt(`Request reinstatement for ${registration}?\n\nOptional notes:`);
    if (notes === null) return; // User cancelled

    try {
      await workstreamAPI.requestReinstatement(vehicleId, notes || undefined);
      alert('Reinstatement request submitted successfully. Admin will review your request.');
      setVehicles(prev => prev.map(v => v.id === vehicleId ? { ...v, hasPendingReinstatement: true } : v));
    } catch (error: any) {
      console.error('Error requesting reinstatement:', error);
      alert(error.response?.data?.message || 'Failed to submit reinstatement request');
    }
  };

  const handleRequestRemoval = async (vehicleId: number, registration: string) => {
    const notes = prompt(`Request removal from cover for ${registration}?\n\nPlease provide a reason:`);
    if (notes === null) return; // User cancelled

    try {
      await workstreamAPI.requestRemoval(vehicleId, notes || undefined);
      alert('Removal request submitted successfully. Admin will review your request.');
      loadVehicles();
    } catch (error: any) {
      console.error('Error requesting removal:', error);
      alert(error.response?.data?.message || 'Failed to submit removal request');
    }
  };

  const handleDownloadCurrentFleet = () => {
    let rows = vehicles.filter(v => {
      if (searchFilter) {
        const s = searchFilter.toLowerCase();
        if (!(
          v.registration.toLowerCase().includes(s) ||
          v.make.toLowerCase().includes(s) ||
          v.model.toLowerCase().includes(s)
        )) return false;
      }
      if (filterCoverStatus) {
        if (filterCoverStatus === 'AwaitingInception') {
          const isAwaiting = v.coverStatus === 'Active' && v.inceptionDate != null &&
            new Date(v.inceptionDate).setHours(0,0,0,0) > new Date().setHours(0,0,0,0);
          if (!isAwaiting) return false;
        } else if (filterCoverStatus === 'Active') {
          const isFuture = v.inceptionDate != null &&
            new Date(v.inceptionDate).setHours(0,0,0,0) > new Date().setHours(0,0,0,0);
          if (v.coverStatus !== 'Active' || isFuture) return false;
        } else {
          if (v.coverStatus !== filterCoverStatus) return false;
        }
      }
      if (filterSiteId) {
        if (filterSiteId === '-1' && v.siteId != null) return false;
        if (filterSiteId !== '-1' && String(v.siteId) !== filterSiteId) return false;
      }
      if (filterCoverType === 'Permanent' && v.tempCoverInfo) return false;
      if (filterCoverType === 'Temporary' && !v.tempCoverInfo) return false;
      return true;
    });
    if (userSiteIds.length > 0) {
      rows = rows.filter(v => v.siteId != null && userSiteIds.includes(v.siteId));
    }
    if (sortRegAZ) {
      rows = [...rows].sort((a, b) => a.registration.localeCompare(b.registration));
    }
    const getStatusText = (v: Vehicle) => {
      if (v.coverStatus === 'Active') {
        const isFuture = v.inceptionDate != null &&
          new Date(v.inceptionDate).setHours(0,0,0,0) > new Date().setHours(0,0,0,0);
        return isFuture ? 'Awaiting Inception' : 'On Cover';
      }
      if (v.coverStatus === 'Pending Removal') return 'Pending Removal';
      return v.coverStatus || 'Cover Pending';
    };
    const getLocation = (v: Vehicle) => {
      if (v.site) return v.site.name;
      if (v.client?.city) return `Head Office (${v.client.city})`;
      return 'Head Office';
    };
    const data = rows.map(v => ({
      'Registration':  v.registration,
      'Make':          v.make,
      'Model':         v.model,
      'Year':          v.year,
      'Cover Status':  getStatusText(v),
      'Cover Type':    v.tempCoverInfo ? 'Temp' : 'Perm',
      'Site':          getLocation(v),
      'Value':         v.vehicleValue != null ? `\u00a3${v.vehicleValue.toLocaleString('en-GB')}` : '\u2014',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), 'Fleet');
    XLSX.writeFile(wb, `Fleet_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleSilentRemove = async (vehicle: any) => {
    if (!window.confirm(
      `Silent Remove: ${vehicle.registration}\n\nThis will immediately deactivate this vehicle.\nNO emails will be sent to the client.\n\nSuperAdmin use only — continue?`
    )) return;
    try {
      await vehiclesAPI.silentRemove(vehicle.id);
      setVehicles(prev => prev.filter(v => v.id !== vehicle.id));
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Failed to silently remove vehicle');
    }
  };

  const handleSendVehicleNotification = async (vehicle: Vehicle) => {
    const notificationType = vehicle.coverStatus === 'Inactive' || vehicle.coverStatus === 'Pending Removal'
      ? 'removal'
      : 'addition';
    if (!window.confirm(
      `Send ${notificationType} notification for ${vehicle.registration}?\n\nThis sends the standard email only to your signed-in SuperAdmin email address and does not change cover status.`
    )) return;
    try {
      const response = await vehiclesAPI.sendNotification(vehicle.id);
      alert(response.data.message || 'Vehicle notification sent.');
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Failed to send vehicle notification.');
    }
  };

  const handleViewHistory = async (vehicleId: number) => {
    setHistoryNote('');
    setLoadingHistory(true);
    setShowHistoryModal(true);
    try {
      const response = await vehiclesAPI.getHistory(vehicleId);
      setHistoryData(response.data);
    } catch (error) {
      console.error('Error loading vehicle history:', error);
      alert('Failed to load vehicle history');
      setShowHistoryModal(false);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleToggleVehicleSelection = (vehicleId: number) => {
    const newSelection = new Set(selectedVehicles);
    if (newSelection.has(vehicleId)) {
      newSelection.delete(vehicleId);
    } else {
      newSelection.add(vehicleId);
    }
    setSelectedVehicles(newSelection);
  };

  const handleSelectAllVehicles = () => {
    if (selectedVehicles.size === vehicles.length) {
      setSelectedVehicles(new Set());
    } else {
      setSelectedVehicles(new Set(vehicles.map(v => v.id)));
    }
  };

  const handleBulkDateAmendment = async () => {
    if (selectedVehicles.size === 0) {
      alert('Please select at least one vehicle');
      return;
    }

    if (!bulkAmendDate) {
      alert('Please select a date');
      return;
    }

    setBulkAmendSubmitting(true);
    try {
      const vehicleIds = Array.from(selectedVehicles);
      // Single bulk request — avoids flooding the server with one PUT per vehicle
      await adminUtilityAPI.bulkUpdateInceptionDates(vehicleIds, bulkAmendDate);
      alert(`Successfully updated ${selectedVehicles.size} vehicle(s)`);
      setShowBulkDateModal(false);
      // Update dates in-place — no full reload needed
      const updatedIds = new Set(selectedVehicles);
      setVehicles(prev => prev.map(v => updatedIds.has(v.id) ? { ...v, inceptionDate: bulkAmendDate } : v));
      setSelectedVehicles(new Set());
    } catch (error: any) {
      console.error('Error updating vehicles:', error);
      alert(error.response?.data?.message || 'Failed to update vehicles');
    } finally {
      setBulkAmendSubmitting(false);
    }
  };


  const openAddVehicleForm = () => {
    // Pre-select the first site ever added to this client (lowest id = oldest)
    const clientSites = isAdmin
      ? sites.filter(s => s.clientId === viewClientId)
      : sites;
    const firstSiteId = clientSites.slice().sort((a, b) => a.id - b.id)[0]?.id;
    setFormData(prev => ({ ...prev, siteId: firstSiteId }));
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      registration: '',
      make: '',
      model: '',
      vehicleType: '',
      weightClass: '',
      grossVehicleWeightKg: undefined,
      year: new Date().getFullYear().toString(),
      clientId: 0,
      siteId: undefined,
      vehicleValue: '',
      hasModifications: '',
      modificationDetails: '',
      inceptionDate: '',
      registeredOwnerIsBusiness: '',
      registeredOwnerBusinessName: '',
      registeredOwnerName: '',
      registeredOwnerAddress: '',
      registeredKeeperIsBusiness: '',
      registeredKeeperBusinessName: '',
      registeredKeeperName: '',
      registeredKeeperAddress: '',
    });
    setShowModifications(false);
    setEditingVehicle(null);
    setShowForm(false);
    setAddVehicleStatusRemoved('');
    setAddVehicleStatusVehicleId('');
    setAddVehicleRemovalDT('');
    setBusinessIsOwnerAndKeeper('');
    setBusinessIsOwnerOtherDetails('');
    setShowPermVehicleSummary(false);
    setPermTcConfirmed(false);
    setSuppressVehicleNotifications(false);
    setVrmLookupResult('none');
  };

  const closeTempCoverModal = () => {
    setShowTempCoverModal(false);
    setTempVehicleStatusRemoved('');
    setTempVehicleStatusVehicleId('');
    setTempVehicleValue('');
    setTempFormReg(''); setTempFormMake(''); setTempFormModel('');
    setTempFormYear(''); setTempFormVehicleType(''); setTempFormWeightClass('');
    setTempGrossVehicleWeightKg(undefined);
    setTempVrmLookupResult('none');
    setTempCoverStartDT('');
    setTempCoverEndDT('');
    setTempBusinessIsOwnerAndKeeper('');
    setTempBusinessIsOwnerOtherDetails('');
    setTempHasModifications('');
    setTempModificationDetails('');
    setTempSiteIdRaw('');
    setTempVehicleCategory('');
    setShowTempSummary(false);
    setTempTcConfirmed(false);
  };

  const handleVrmLookup = async (reg: string, form: 'add' | 'temp' | 'change') => {
    const cleanReg = reg.replace(/\s/g, '').toUpperCase();
    if (!cleanReg) { alert('Please enter a registration number first.'); return; }
    setVrmLookupLoading(true);
    try {
      const res = await vehiclesAPI.lookupVrm(cleanReg);
      const d = res.data;
      if (form === 'add') {
        setFormData(prev => ({
          ...prev,
          make:        d.make        || prev.make,
          model:       d.model       || prev.model,
          year:        d.year        || prev.year,
          vehicleType: d.vehicleType || prev.vehicleType,
          weightClass: d.weightClass || prev.weightClass,
          grossVehicleWeightKg: d.grossVehicleWeightKg ?? prev.grossVehicleWeightKg,
        }));
        setVrmLookupResult('found');
      } else if (form === 'temp') {
        if (d.make)        setTempFormMake(d.make);
        if (d.model)       setTempFormModel(d.model);
        if (d.year)        setTempFormYear(d.year);
        if (d.vehicleType) setTempFormVehicleType(d.vehicleType);
        if (d.weightClass) setTempFormWeightClass(d.weightClass);
        setTempGrossVehicleWeightKg(d.grossVehicleWeightKg);
        setTempVrmLookupResult('found');
      } else {
        if (d.make)        setChangeFormMake(d.make);
        if (d.model)       setChangeFormModel(d.model);
        if (d.year)        setChangeFormYear(d.year);
        if (d.vehicleType) setChangeFormVehicleType(d.vehicleType);
        if (d.weightClass) setChangeFormWeightClass(d.weightClass);
        setChangeVrmLookupResult('found');
      }
    } catch (err: any) {
      // 404 = not found — show inline banner, don't alert
      if (err.response?.status === 404) {
        if (form === 'add')    setVrmLookupResult('notfound');
        else if (form === 'temp') setTempVrmLookupResult('notfound');
        else setChangeVrmLookupResult('notfound');
      } else {
        alert(err.response?.data?.message || 'Lookup failed. Please try again.');
      }
    } finally {
      setVrmLookupLoading(false);
    }
  };

  if (loading) {
    return <div>Loading vehicles...</div>;
  }
  const selectedUploadClient = clients.find((client: any) => client.id === selectedClientId);
  const isInitialOnboardingEligible = canBulkUpload && selectedClientId > 0 && selectedUploadClient?.vehicleCount === 0;

  return (
    <div className="vehicle-list">
      <div className="page-header">
        <h1>Vehicles</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {canBulkUpload && (
            <button className="btn btn-secondary" onClick={() => setShowCsvUpload(!showCsvUpload)}>
              📄 Bulk Upload CSV
            </button>
          )}
          {!isAdmin && (
            <>
              <button className="btn btn-primary" onClick={openAddVehicleForm} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                <img src={additionIcon} alt="" style={{width: '16px', height: '16px', flexShrink: 0}} />
                <span style={{ flex: 1, textAlign: 'center' }}>Add Perm Vehicle</span>
                <span style={{ width: '16px', flexShrink: 0 }} />
              </button>
              <button className="btn btn-primary" onClick={() => setShowTempCoverModal(true)}>
                ⏱️ Add Temp Vehicle
              </button>
              <button className="btn btn-primary" onClick={() => setShowRemoveVehicleModal(true)} style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}>
                🛑 Remove Vehicle
              </button>
              {sites.length > 1 && (
                <button className="btn btn-primary" onClick={() => setShowChangeSiteModal(true)}>
                  📍 Change of Site
                </button>
              )}
            </>
          )}
          {isAdmin && (
            <>
              <button className="btn btn-primary" onClick={openAddVehicleForm} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                <img src={additionIcon} alt="" style={{width: '16px', height: '16px', flexShrink: 0}} />
                <span style={{ flex: 1, textAlign: 'center' }}>Add Vehicle</span>
                <span style={{ width: '16px', flexShrink: 0 }} />
              </button>
              {isSuperAdmin && viewClientId > 0 && (
                <button className="btn btn-primary" onClick={() => setShowRemoveVehicleModal(true)} style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}>
                  🛑 Remove Vehicle
                </button>
              )}
              {viewClientId > 0 && vehicles.length > 0 && (
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowBulkDateModal(true)}
                  disabled={selectedVehicles.size === 0}
                >
                  📅 Bulk Amend Dates ({selectedVehicles.size})
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px' }}>Select Client to View Vehicles</h3>
          <div className="form-group">
            <label htmlFor="vehicle-client-selector">Client *</label>
            <select
              id="vehicle-client-selector"
              value={viewClientId}
              onChange={(e) => setViewClientId(parseInt(e.target.value))}
              style={{ width: '100%', padding: '12px', fontSize: '14px', borderRadius: '8px', border: '2px solid #e2e8f0' }}
            >
              <option value="0">── Select a client ──</option>
              {clients.map((client: any) => (
                <option key={client.id} value={client.id}>
                  {client.companyName || client.CompanyName}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {isAdmin && !canBulkUpload && (
        <div style={{ margin: '0 0 24px', padding: '16px 20px', background: '#fffbeb', border: '1.5px solid #fbbf24', borderRadius: 10, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <span style={{ fontSize: 22 }}>&#128274;</span>
          <div>
            <div style={{ fontWeight: 700, color: '#92400e', fontSize: 15, marginBottom: 4 }}>Bulk Upload — Authorised Staff Access Required</div>
            <div style={{ color: '#78350f', fontSize: 14 }}>Bulk vehicle uploads must be processed by a <strong>Super Admin or Onboarder</strong>. Please contact the Yourshield onboarding team to have the upload carried out on your behalf.</div>
          </div>
        </div>
      )}

      {showCsvUpload && canBulkUpload && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px' }}>Bulk Upload Vehicles from CSV</h3>
          {isOnboarder && (
            <div style={{ marginBottom: 16, padding: '12px 14px', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, color: '#1e3a8a', fontSize: 13, lineHeight: 1.5 }}>
              Historical inception dates are allowed for onboarding uploads and are automatically recorded in the onboarding audit trail.
            </div>
          )}
          
          <div className="form-group" style={{ marginBottom: '16px' }}>
            <label>Upload Mode *</label>
            <select
              value={uploadMode}
              onChange={(e) => setUploadMode(e.target.value as 'single-date' | 'custom-dates')}
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            >
              <option value="single-date">Single Inception Date (all vehicles same date)</option>
              <option value="custom-dates">Custom Dates (each vehicle has own inception date in CSV)</option>
            </select>
            <small style={{ display: 'block', marginTop: '4px', color: '#718096', fontSize: '12px' }}>
              {uploadMode === 'single-date' 
                ? 'All vehicles will use the same inception date you specify below' 
                : 'Each vehicle row in CSV must include its own InceptionDate column'}
            </small>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <button className="btn btn-secondary" onClick={handleDownloadTemplate}>
              ⬇️ Download CSV Template ({uploadMode === 'single-date' ? 'Standard' : 'With Dates'})
            </button>
            <small style={{ marginLeft: '12px', color: '#718096' }}>
              Download the template with the correct format and example data
            </small>
          </div>

          <div className="form-group" style={{ marginBottom: '16px' }}>
            <label>Select Client *</label>
            <select
              value={selectedClientId}
              onChange={(e) => { setSelectedClientId(parseInt(e.target.value)); setInitialOnboardingUpload(false); }}
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            >
              <option value="0">Select a client...</option>
              {clients.map((client: any) => (
                <option key={client.id} value={client.id}>
                  {client.companyName || client.CompanyName}
                </option>
              ))}
            </select>
          </div>
          {isInitialOnboardingEligible && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, padding: '14px 16px', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={initialOnboardingUpload} onChange={event => setInitialOnboardingUpload(event.target.checked)} style={{ marginTop: 3, width: 17, height: 17 }} />
              <span>
                <strong style={{ display: 'block', color: '#1e3a8a' }}>Is this the client&apos;s first vehicle upload?</strong>
                <span style={{ display: 'block', marginTop: 4, color: '#334155', fontSize: 13, lineHeight: 1.5 }}>Confirm to add this initial fleet directly to Active cover without client or MTA emails, Workstream approval, or a cover letter. Later uploads will follow the normal notification process.</span>
              </span>
            </label>
          )}

          {uploadMode === 'single-date' && (
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label>Bulk Inception Date (Cover Start Date) *</label>
              <input
                type="date"
                value={bulkInceptionDate}
                onChange={(e) => setBulkInceptionDate(e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              />
              <small style={{ display: 'block', marginTop: '4px', color: '#718096', fontSize: '12px' }}>
                All vehicles in this upload will have this inception date. Cover end date will be client's policy end date.
              </small>
            </div>
          )}

          <div className="form-group" style={{ marginBottom: '16px' }}>
            <label>Upload CSV File *</label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            />
            {csvFile && (
              <small style={{ display: 'block', marginTop: '8px', color: '#28a745' }}>
                ✓ Selected: {csvFile.name}
              </small>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              className="btn btn-primary"
              onClick={handleCsvUpload}
              disabled={!csvFile || selectedClientId === 0}
            >
              🚀 Upload Vehicles
            </button>
            
            {uploadResult?.uploadBatchId && uploadResult.successCount > 0 && (
              <button
                className="btn btn-secondary"
                onClick={handleUndoUpload}
                style={{ backgroundColor: '#dc3545', color: 'white', border: 'none' }}
              >
                ↩️ Undo Last Upload
              </button>
            )}
            
            {selectedClientId > 0 && (
              <button
                className="btn btn-secondary"
                onClick={async () => {
                  if (window.confirm(`Reactivate ALL inactive vehicles for this client?\n\nThis will restore all previously deleted/undone vehicles.`)) {
                    try {
                      const response = await vehiclesAPI.reactivateClientVehicles(selectedClientId);
                      alert(response.data.message);
                      loadVehicles();
                    } catch (error: any) {
                      console.error('Error reactivating vehicles:', error);
                      alert(error.response?.data?.message || 'Failed to reactivate vehicles');
                    }
                  }
                }}
                style={{ backgroundColor: '#28a745', color: 'white', border: 'none' }}
              >
                ♻️ Reactivate Vehicles
              </button>
            )}
          </div>

          {uploadResult && (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              borderRadius: '8px',
              backgroundColor: uploadResult.successCount > 0 ? '#d4edda' : '#f8d7da',
              border: `1px solid ${uploadResult.successCount > 0 ? '#c3e6cb' : '#f5c6cb'}`
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: uploadResult.successCount > 0 ? '#155724' : '#721c24' }}>
                Upload Results
              </h4>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                <strong>{uploadResult.successCount}</strong> vehicle(s) uploaded successfully
              </p>
              {uploadResult.errorCount > 0 && (
                <>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#721c24' }}>
                    <strong>{uploadResult.errorCount}</strong> error(s) encountered
                  </p>
                  <div style={{
                    maxHeight: '200px',
                    overflow: 'auto',
                    backgroundColor: 'white',
                    padding: '12px',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}>
                    {uploadResult.errors.map((error: string, index: number) => (
                      <div key={index} style={{ color: '#721c24', marginBottom: '4px' }}>
                        ⚠️ {error}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>{editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}</h2>
              <button className="close-btn" onClick={resetForm}>✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* ── Form fields — hidden during T&C summary review ── */}
              {(!showPermVehicleSummary || editingVehicle || isAdmin) && (<>
              <div className="form-group">
                <label style={{ textAlign: 'center', display: 'block' }}>Registration Number *</label>
                <div className="uk-plate-container">
                  <div className="uk-plate-wrapper">
                    <div className="uk-plate-strip">
                      <img src={shieldIcon} alt="" className="uk-plate-shield-icon" />
                      <span className="uk-plate-stars">★★★</span>
                      <span className="uk-plate-gb">GB</span>
                    </div>
                    <input
                      type="text"
                      className="uk-plate-input"
                      required
                      value={formData.registration}
                      onChange={(e) => setFormData({ ...formData, registration: e.target.value.toUpperCase().replace(/[^A-Z0-9 ]/g, '') })}
                      placeholder="AB12 CDE"
                      maxLength={8}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleVrmLookup(formData.registration, 'add')}
                    disabled={vrmLookupLoading || !formData.registration.trim()}
                  >
                    {vrmLookupLoading ? 'Searching...' : 'Search Registration'}
                  </button>
                </div>
              </div>

              {/* VRM result banner */}
              {vrmLookupResult === 'found' && (
                <div className="vrm-result-banner vrm-result-banner--found" style={{ marginBottom: 16, flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <span>✓ Vehicle data verified by UKVD — is this the correct vehicle?</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="vrm-confirm-btn vrm-confirm-btn--yes" onClick={() => setVrmLookupResult('confirmed')}>✓ Yes, correct</button>
                    <button type="button" className="vrm-confirm-btn vrm-confirm-btn--no" onClick={() => {
                      setVrmLookupResult('rejected');
                      setFormData(prev => ({...prev, make:'', model:'', year: new Date().getFullYear().toString(), vehicleType:'', weightClass:''}));
                    }}>✗ No, enter manually</button>
                  </div>
                </div>
              )}
              {vrmLookupResult === 'confirmed' && (
                <div className="vrm-result-banner vrm-result-banner--found" style={{ marginBottom: 16 }}>
                  ✓ Confirmed — vehicle details locked
                </div>
              )}
              {vrmLookupResult === 'rejected' && (
                <div className="vrm-result-banner vrm-result-banner--rejected" style={{ marginBottom: 16 }}>
                  ✗ Please enter the vehicle details manually below
                </div>
              )}
              {vrmLookupResult === 'notfound' && (
                <div className="vrm-result-banner vrm-result-banner--notfound" style={{ marginBottom: 16 }}>
                  ⚠ Vehicle not found — please enter the details below manually
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Make *</label>
                  <input
                    type="text"
                    required
                    readOnly={vrmLookupResult === 'found' || vrmLookupResult === 'confirmed'}
                    className={(vrmLookupResult === 'found' || vrmLookupResult === 'confirmed') ? 'vrm-locked-field' : undefined}
                    value={formData.make}
                    onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                    placeholder="e.g. Ford"
                  />
                </div>

                <div className="form-group">
                  <label>Model *</label>
                  <input
                    type="text"
                    required
                    readOnly={vrmLookupResult === 'found' || vrmLookupResult === 'confirmed'}
                    className={(vrmLookupResult === 'found' || vrmLookupResult === 'confirmed') ? 'vrm-locked-field' : undefined}
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="e.g. Focus"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Vehicle Type *</label>
                  <select
                    required
                    className={(vrmLookupResult === 'found' || vrmLookupResult === 'confirmed') ? 'vrm-locked-field' : undefined}
                    value={formData.vehicleType}
                    onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })}
                  >
                    <option value="">Select vehicle type...</option>
                    {formData.vehicleType.includes(' (') && (
                      <option value={formData.vehicleType}>{formData.vehicleType}</option>
                    )}
                    <option value="Car">Car</option>
                    <option value="Van">Van</option>
                    <option value="Truck">Truck</option>
                    <option value="Bus">Bus</option>
                    <option value="Special Type">Special Type</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Weight Class *</label>
                  <select
                    required
                    className={(vrmLookupResult === 'found' || vrmLookupResult === 'confirmed') ? 'vrm-locked-field' : undefined}
                    value={formData.weightClass}
                    onChange={(e) => setFormData({ ...formData, weightClass: e.target.value })}
                  >
                    <option value="">Select weight class...</option>
                    {formData.weightClass.includes(' (') && (
                      <option value={formData.weightClass}>{formData.weightClass}</option>
                    )}
                    <option value="Car">Car</option>
                    <option value="Up to 3.5T">Up to 3.5T</option>
                    <option value="Up to 7.5T">Up to 7.5T</option>
                    <option value="Up to 18T">Up to 18T</option>
                    <option value="Up to 26T">Up to 26T</option>
                    <option value="Up to 40T">Up to 40T</option>
                    <option value="More than 40T">More than 40T</option>
                    <option value="Up to ABI 10">Up to ABI 10</option>
                    <option value="Up to ABI 20">Up to ABI 20</option>
                    <option value="Up to ABI 30">Up to ABI 30</option>
                    <option value="Up to ABI 40">Up to ABI 40</option>
                    <option value="Up to ABI 50">Up to ABI 50</option>
                    <option value="Non Rated ABI">Non Rated ABI</option>
                    <option value="Up to 16 Seater">Up to 16 Seater</option>
                    <option value="Up to 29 Seater">Up to 29 Seater</option>
                    <option value="Up to 53 Seater">Up to 53 Seater</option>
                    <option value="More than 53 Seater">More than 53 Seater</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Year *</label>
                  <input
                    type="text"
                    required
                    readOnly={vrmLookupResult === 'found' || vrmLookupResult === 'confirmed'}
                    className={(vrmLookupResult === 'found' || vrmLookupResult === 'confirmed') ? 'vrm-locked-field' : undefined}
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    placeholder="e.g. 2024"
                    maxLength={4}
                  />
                </div>

                <div className="form-group">
                  <label>Site/Location *</label>
                  {(() => {
                    const clientSites = sites
                      .filter(s => isAdmin ? s.clientId === viewClientId : true)
                      .slice()
                      .sort((a, b) => a.id - b.id);
                    return (
                      <>
                        <select
                          value={formData.siteId || ''}
                          onChange={e => setFormData({ ...formData, siteId: e.target.value ? parseInt(e.target.value) : undefined })}
                        >
                          {clientSites.length === 0
                            ? <option value="">\u2014 No sites configured \u2014</option>
                            : clientSites.map(site => (
                                <option key={site.id} value={site.id}>{site.name}</option>
                              ))
                          }
                        </select>
                        <small style={{ color: '#718096', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          {clientSites.length === 0
                            ? 'No sites configured for this client \u2014 contact your administrator.'
                            : `${clientSites.length} site${clientSites.length !== 1 ? 's' : ''} available \u2014 defaulting to the first site added.`}
                        </small>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Vehicle Category — only shown for clients with requiresVehicleCategory enabled */}
              {clientRequiresVehicleCategory && !isAdmin && (
                <div className="form-group" style={{ padding: '14px 16px', background: '#f0f9ff', border: '2px solid #bae6fd', borderRadius: '8px', marginBottom: '16px' }}>
                  <label style={{ fontWeight: 700, color: '#0369a1' }}>Vehicle Category *</label>
                  <select
                    required
                    value={vehicleCategory}
                    onChange={(e) => setVehicleCategory(e.target.value)}
                    style={{ marginTop: 6 }}
                  >
                    <option value="">Select category...</option>
                    <option value="TAV">TAV — Temporary Additional Vehicle</option>
                    <option value="Contract Hire">Contract Hire</option>
                  </select>
                  <small style={{ color: '#0369a1', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Your account requires a vehicle category to be selected for all submissions.
                  </small>
                </div>
              )}

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                  Does the vehicle have any modifications? *
                  <InfoTooltip
                    label="What are modifications?"
                    content={<div>
                      <p style={{ margin: '0 0 8px' }}>Modifications are any changes made to the vehicle <strong>after it left the manufacturer</strong>, including:</p>
                      <ul style={{ margin: '0 0 8px', paddingLeft: 16, lineHeight: 1.8 }}>
                        <li>Engine or performance upgrades</li>
                        <li>Body kits, spoilers or non-standard bodywork</li>
                        <li>Suspension or non-standard wheel/tyre changes</li>
                        <li>Custom or aftermarket exhaust systems</li>
                        <li>Non-factory paintwork or wraps</li>
                        <li>Audio, lighting or electrical upgrades</li>
                        <li>Tow bars or roof racks (if non-standard)</li>
                      </ul>
                      <p style={{ margin: '0 0 6px', fontSize: 12, color: '#94a3b8' }}>This list is not exhaustive — any non-standard changes to the vehicle should be declared, regardless of whether they appear above.</p>
                      <p style={{ margin: 0, fontSize: 12, color: '#fca5a5' }}>⚠️ All modifications must be declared. Failure to disclose may result in a claim being declined or cover being void.</p>
                    </div>}
                  />
                </label>
                <select
                  required
                  value={formData.hasModifications}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, hasModifications: value });
                    setShowModifications(value === 'yes');
                    if (value !== 'yes') {
                      setFormData(prev => ({ ...prev, modificationDetails: '' }));
                    }
                  }}
                >
                  <option value="">Select...</option>
                  <option value="no">N — No Modifications</option>
                  <option value="yes">Y — Has Modifications</option>
                </select>
              </div>

              {showModifications && (
                <div className="form-group" style={{ 
                  backgroundColor: '#f7fafc', 
                  padding: '16px', 
                  borderRadius: '8px',
                  border: '2px solid #e2e8f0',
                  marginBottom: '20px'
                }}>
                  <label>Modification Details *</label>
                  <textarea
                    required={showModifications}
                    value={formData.modificationDetails}
                    onChange={(e) => setFormData({ ...formData, modificationDetails: e.target.value })}
                    placeholder="Please describe all modifications to the vehicle..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '14px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e0',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                  <small style={{color: '#718096', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                    Include details such as engine modifications, body kit, suspension changes, etc.
                  </small>
                </div>
              )}

              {/* ── Registered Owner / Keeper ─────────────────────────── */}
              {!isAdmin ? (
                <div style={{ marginTop: 20, padding: '14px 16px', background: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: 8, marginBottom: 20 }}>
                  <div className="form-group" style={{ marginBottom: 4 }}>
                    <label style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                      Vehicle Ownership *
                      <InfoTooltip
                        label="Vehicle Ownership Explained"
                        content={<div>
                          <p style={{ margin: '0 0 7px', fontWeight: 600 }}>Registered Keeper</p>
                          <p style={{ margin: '0 0 10px' }}>The person/company on the V5C (logbook) responsible for licensing the vehicle.</p>
                          <p style={{ margin: '0 0 7px', fontWeight: 600 }}>Vehicle Owner</p>
                          <p style={{ margin: '0 0 10px' }}>The person/company who holds legal title to the vehicle and can sell it.</p>
                          <p style={{ margin: '0 0 7px', fontWeight: 600 }}>Long-Term Lease (12+ months)</p>
                          <p style={{ margin: '0 0 10px' }}>Where ownership stays with the leasing company but the vehicle is in your possession under a 12+ month agreement. This is treated as a standard business vehicle.</p>
                          <p style={{ margin: 0, fontSize: 12, color: '#fca5a5' }}>Other arrangements (e.g. short-term hire, individually-owned vehicles) must be declared and will be referred for review.</p>
                        </div>}
                      />
                    </label>
                    <select
                      required
                      value={businessIsOwnerAndKeeper}
                      onChange={(e) => {
                        const val = e.target.value as '' | 'yes' | 'lease' | 'other';
                        setBusinessIsOwnerAndKeeper(val);
                        setBusinessIsOwnerOtherDetails('');
                        if (val === 'yes') {
                          // Business owns and keeps — auto-approve
                          setFormData(prev => ({
                            ...prev,
                            registeredOwnerIsBusiness: 'yes',
                            registeredOwnerBusinessName: clientDetails?.name || '',
                            registeredOwnerAddress: clientDetails?.address || '',
                            registeredOwnerName: '',
                            registeredKeeperIsBusiness: 'yes',
                            registeredKeeperBusinessName: clientDetails?.name || '',
                            registeredKeeperAddress: clientDetails?.address || '',
                            registeredKeeperName: '',
                          }));
                        } else if (val === 'lease') {
                          // Leasing company owns, business is keeper — auto-approve
                          setFormData(prev => ({
                            ...prev,
                            registeredOwnerIsBusiness: '',   // null — leasing co. owns it
                            registeredOwnerBusinessName: '',
                            registeredOwnerAddress: '',
                            registeredOwnerName: '',
                            registeredKeeperIsBusiness: 'yes', // business IS the keeper
                            registeredKeeperBusinessName: clientDetails?.name || '',
                            registeredKeeperAddress: clientDetails?.address || '',
                            registeredKeeperName: '',
                          }));
                        } else if (val === 'other') {
                          // Non-standard — triggers MTA referral
                          setFormData(prev => ({
                            ...prev,
                            registeredOwnerIsBusiness: 'no',
                            registeredOwnerBusinessName: '',
                            registeredOwnerAddress: '',
                            registeredOwnerName: '',
                            registeredKeeperIsBusiness: 'no',
                            registeredKeeperBusinessName: '',
                            registeredKeeperAddress: '',
                            registeredKeeperName: '',
                          }));
                        } else {
                          setFormData(prev => ({ ...prev, registeredOwnerIsBusiness: '', registeredKeeperIsBusiness: '' }));
                        }
                      }}
                    >
                      <option value="">Select...</option>
                      <option value="yes">{clientDetails?.name ? `Yes, ${clientDetails.name} is the registered keeper or owner` : 'Yes, we are the registered keeper or owner'}</option>
                      <option value="lease">No, the vehicle is leased or hired on a long-term hire agreement (12+ months)</option>
                      <option value="other">Other (please specify)</option>
                    </select>
                  </div>
                  {businessIsOwnerAndKeeper === 'other' && (
                    <div className="form-group" style={{ marginTop: 10, marginBottom: 0 }}>
                      <label style={{ fontSize: 13 }}>Please describe the ownership arrangement *</label>
                      <textarea
                        required
                        rows={2}
                        value={businessIsOwnerOtherDetails}
                        onChange={e => setBusinessIsOwnerOtherDetails(e.target.value)}
                        placeholder="e.g. Vehicle is on a short-term hire, owned by a director, etc."
                        style={{ width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #cbd5e0', resize: 'vertical', fontFamily: 'inherit' }}
                      />
                      <div style={{ marginTop: 8, padding: '10px 14px', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 6, fontSize: 13, color: '#92400e' }}>
                        ⚠️ This vehicle will be referred to your Myshield handler for review before cover is confirmed.
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* ── Registered Owner (admin) ──────────────────────────── */}
                  <div style={{ marginTop: 20, padding: '14px 16px', background: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: 8, marginBottom: 0 }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#1a202c', fontWeight: 700 }}>Registered Owner</h4>
                    <div className="form-group" style={{ marginBottom: 12 }}>
                      <label>Is the business the Registered Owner? *</label>
                      <select required value={formData.registeredOwnerIsBusiness}
                        onChange={(e) => { const val = e.target.value as '' | 'yes' | 'no'; setFormData({ ...formData, registeredOwnerIsBusiness: val, registeredOwnerBusinessName: val === 'yes' ? (clientDetails?.name || '') : '', registeredOwnerAddress: val === 'yes' ? (clientDetails?.address || '') : '', registeredOwnerName: '' }); }}
                      >
                        <option value="">Select...</option>
                        <option value="yes">Y — Yes, business is the Registered Owner</option>
                        <option value="no">N — No, a named individual is the Registered Owner</option>
                      </select>
                    </div>
                    {formData.registeredOwnerIsBusiness === 'yes' && (
                      <>
                        <div className="form-group" style={{ marginBottom: 10 }}>
                          <label>Business Name</label>
                          <input type="text" readOnly value={formData.registeredOwnerBusinessName} className="vrm-locked-field" />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label>Registered Address (Head Office)</label>
                          <textarea readOnly rows={3} value={formData.registeredOwnerAddress} className="vrm-locked-field"
                            style={{ width: '100%', padding: '8px 10px', fontSize: 14, borderRadius: 6, border: '1px solid #cbd5e0', resize: 'none', fontFamily: 'inherit', background: '#f7fafc', color: '#4a5568' }} />
                          <small style={{ color: '#718096', fontSize: 12, marginTop: 4, display: 'block' }}>Auto-filled from your company's registered head office address</small>
                        </div>
                      </>
                    )}
                    {formData.registeredOwnerIsBusiness === 'no' && (
                      <>
                        <div className="form-group" style={{ marginBottom: 12 }}>
                          <label>Owner Full Name *</label>
                          <input type="text" required value={formData.registeredOwnerName} onChange={(e) => setFormData({ ...formData, registeredOwnerName: e.target.value })} placeholder="e.g. John Smith" />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label>Owner Address *</label>
                          <textarea required rows={3} value={formData.registeredOwnerAddress} onChange={(e) => setFormData({ ...formData, registeredOwnerAddress: e.target.value })} placeholder="House number & street, town, postcode"
                            style={{ width: '100%', padding: '8px 10px', fontSize: 14, borderRadius: 6, border: '1px solid #cbd5e0', resize: 'vertical', fontFamily: 'inherit' }} />
                        </div>
                      </>
                    )}
                  </div>
                  {/* ── Registered Keeper (admin) ─────────────────────────── */}
                  <div style={{ marginTop: 12, padding: '14px 16px', background: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: 8, marginBottom: 20 }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#1a202c', fontWeight: 700 }}>Registered Keeper</h4>
                    <div className="form-group" style={{ marginBottom: 12 }}>
                      <label>Is the business the Registered Keeper? *</label>
                      <select required value={formData.registeredKeeperIsBusiness}
                        onChange={(e) => { const val = e.target.value as '' | 'yes' | 'no'; setFormData({ ...formData, registeredKeeperIsBusiness: val, registeredKeeperBusinessName: val === 'yes' ? (clientDetails?.name || '') : '', registeredKeeperAddress: val === 'yes' ? (clientDetails?.address || '') : '', registeredKeeperName: '' }); }}
                      >
                        <option value="">Select...</option>
                        <option value="yes">Y — Yes, business is the Registered Keeper</option>
                        <option value="no">N — No, a named individual is the Registered Keeper</option>
                      </select>
                    </div>
                    {formData.registeredKeeperIsBusiness === 'yes' && (
                      <>
                        <div className="form-group" style={{ marginBottom: 10 }}>
                          <label>Business Name</label>
                          <input type="text" readOnly value={formData.registeredKeeperBusinessName} className="vrm-locked-field" />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label>Registered Address (Head Office)</label>
                          <textarea readOnly rows={3} value={formData.registeredKeeperAddress} className="vrm-locked-field"
                            style={{ width: '100%', padding: '8px 10px', fontSize: 14, borderRadius: 6, border: '1px solid #cbd5e0', resize: 'none', fontFamily: 'inherit', background: '#f7fafc', color: '#4a5568' }} />
                          <small style={{ color: '#718096', fontSize: 12, marginTop: 4, display: 'block' }}>Auto-filled from your company's registered head office address</small>
                        </div>
                      </>
                    )}
                    {formData.registeredKeeperIsBusiness === 'no' && (
                      <>
                        <div className="form-group" style={{ marginBottom: 12 }}>
                          <label>Keeper Full Name *</label>
                          <input type="text" required value={formData.registeredKeeperName} onChange={(e) => setFormData({ ...formData, registeredKeeperName: e.target.value })} placeholder="e.g. Jane Smith" />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label>Keeper Address *</label>
                          <textarea required rows={3} value={formData.registeredKeeperAddress} onChange={(e) => setFormData({ ...formData, registeredKeeperAddress: e.target.value })} placeholder="House number & street, town, postcode"
                            style={{ width: '100%', padding: '8px 10px', fontSize: 14, borderRadius: 6, border: '1px solid #cbd5e0', resize: 'vertical', fontFamily: 'inherit' }} />
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                  Declared Value (£) *
                  <InfoTooltip
                    label="Help with valuing the vehicle?"
                    content={<div>
                      <p style={{ margin: '0 0 8px' }}>The Declared Value should reflect the vehicle's <strong>current market value</strong>, taking into account:</p>
                      <ul style={{ margin: '0 0 10px', paddingLeft: 16, lineHeight: 1.8 }}>
                        <li><strong>Age</strong> — older vehicles may have depreciated significantly in value</li>
                        <li><strong>Mileage</strong> — higher mileage can lead to a reduced market value</li>
                        <li><strong>Condition</strong> — fair assessment of body &amp; mechanical condition</li>
                        <li><strong>Modifications</strong> — non-standard changes may affect value</li>
                      </ul>
                      <p style={{ margin: 0, fontSize: 12 }}>In the event of a total loss, the insurer uses market value at the time of the incident. A significantly incorrect declaration may affect claim processing.</p>
                    </div>}
                  />
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  required
                  value={formData.vehicleValue}
                  onChange={(e) => setFormData({ ...formData, vehicleValue: e.target.value.replace(/[^0-9]/g, '') })}
                  placeholder="e.g. 15000"
                />
                {(() => { const p = vehicleValuePreview(formData.vehicleValue); return p ? (
                  <div style={{ marginTop: '7px', padding: '8px 12px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', fontSize: '13px', color: '#0c4a6e', fontWeight: 500 }}>
                    {p.monetary} &mdash; {p.words}
                  </div>
                ) : null; })()}
                {clientDetails?.mtaValueThreshold != null && (() => {
                  const v = parseInt(formData.vehicleValue || '0', 10);
                  return v >= (clientDetails!.mtaValueThreshold as number) ? (
                    <div style={{ marginTop: 7, padding: '9px 12px', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 6, fontSize: 13, color: '#92400e', fontWeight: 600 }}>
                      ⚠️ Declared values of £{(clientDetails!.mtaValueThreshold as number).toLocaleString()} or above will be referred to the MTA team for review before cover is confirmed.
                    </div>
                  ) : null;
                })()}
                <small style={{ color: '#718096', fontSize: '12px', marginTop: '6px', display: 'block' }}>
                  All claims are settled at vehicle market value set by the insurer at time of incident
                </small>
                <small style={{ color: '#92400e', fontSize: '12px', marginTop: '3px', display: 'block', fontWeight: 700 }}>
                  ** If vehicle value is materially incorrect, cover may be invalidated.
                </small>
              </div>

              {!editingVehicle && (
                <div className="form-group">
                  <label>Cover Start Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.inceptionDate}
                    onChange={(e) => setFormData({ ...formData, inceptionDate: e.target.value })}
                    min={isAdmin ? undefined : new Date().toISOString().split('T')[0]}
                  />
                  <small style={{color: '#718096', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                    {isAdmin
                      ? `Cover start date determines the cover period. Cover end date is automatically calculated from the client's policy end date${clientPolicyEndDate ? ` (${formatDateBritish(clientPolicyEndDate)})` : ''}.`
                      : `Cover start date cannot be backdated. Any future dated additions will commence from 00:00 on the requested start date.`}
                  </small>
                </div>
              )}

              {!editingVehicle && canSuppressVehicleNotifications && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, padding: '13px 15px', background: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={suppressVehicleNotifications}
                    onChange={event => setSuppressVehicleNotifications(event.target.checked)}
                    style={{ marginTop: 3, width: 17, height: 17 }}
                  />
                  <span style={{ fontSize: 13, lineHeight: 1.5, color: '#92400e' }}>
                    <strong style={{ display: 'block' }}>Do not send vehicle-addition notifications</strong>
                    Use for historical onboarding records only. This suppresses client, insurer, MTA, cover-letter, and submitting-user emails; the vehicle addition and its cover decision remain fully audited.
                  </span>
                </label>
              )}
              {!editingVehicle && (
                <div style={{ marginTop: '20px', padding: '14px 16px', background: '#fefce8', border: '2px solid #fde68a', borderRadius: '8px', marginBottom: '16px' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#92400e' }}>Do you have a vehicle to remove from cover?</h4>
                  <div className="form-group" style={{ marginBottom: addVehicleStatusRemoved === 'yes' ? '12px' : '0' }}>
                    <select
                      value={addVehicleStatusRemoved}
                      onChange={(e) => { setAddVehicleStatusRemoved(e.target.value); setAddVehicleStatusVehicleId(''); }}
                    >
                      <option value="">Select status...</option>
                  <option value="yes">Yes — Vehicle Removed</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  {addVehicleStatusRemoved === 'yes' && (
                    <>
                      <div className="form-group" style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '13px' }}>Select Vehicle to Remove *</label>
                        <select
                          value={addVehicleStatusVehicleId}
                          onChange={(e) => setAddVehicleStatusVehicleId(e.target.value)}
                        >
                          <option value="">Select vehicle...</option>
                          {vehicles.filter(v => v.coverStatus !== 'Inactive').map(v => (
                            <option key={v.id} value={v.id}>
                              {v.registration} — {v.make} {v.model}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: '13px' }}>Removal Date &amp; Time *</label>
                        <input
                          type="datetime-local"
                          value={addVehicleRemovalDT}
                          onChange={(e) => setAddVehicleRemovalDT(e.target.value)}
                          min={isOnboarder || isSuperAdmin ? undefined : new Date().toISOString().slice(0, 16)}
                          required
                          style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
                        />
                        <small style={{ color: '#718096', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          {isOnboarder || isSuperAdmin
                            ? 'Historical removal dates are permitted for onboarding reconciliation and recorded in the audit trail.'
                            : 'Future date only — cannot be in the past'}
                        </small>
                      </div>
                    </>
                  )}
                </div>
              )}
              </>)}

              {/* ── T&C Confirmation Summary — shown for new client perm vehicle submissions ── */}
              {showPermVehicleSummary && !editingVehicle && !isAdmin && (() => {
                const ownerLabel =
                  businessIsOwnerAndKeeper === 'yes' ? 'Yes, registered keeper or owner' :
                  businessIsOwnerAndKeeper === 'lease' ? 'Leased / long-term hire (12+ months)' :
                  businessIsOwnerAndKeeper === 'other' ? `Other — ${businessIsOwnerOtherDetails}` : '—';
                const vVal = parseInt(formData.vehicleValue || '0', 10);
                const vPreview = vehicleValuePreview(formData.vehicleValue);
                const SRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
                  <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9', padding: '7px 0', fontSize: 14 }}>
                    <span style={{ width: '45%', color: '#64748b', fontWeight: 600, flexShrink: 0 }}>{label}</span>
                    <span style={{ color: '#1e293b' }}>{value}</span>
                  </div>
                );
                const siteName = formData.siteId
                  ? (sites.find(s => s.id === formData.siteId)?.name || `Site #${formData.siteId}`)
                  : 'Head Office (Default)';
                return (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                      <h4 style={{ margin: '0 0 12px', fontSize: 15, color: '#0c4a6e', fontWeight: 700 }}>
                        📋 Please review your vehicle submission
                      </h4>
                      <SRow label="Registration" value={<strong>{formData.registration}</strong>} />
                      <SRow label="Make / Model" value={`${formData.make} ${formData.model} (${formData.year})`} />
                      <SRow label="Vehicle Type" value={formData.vehicleType || '—'} />
                      <SRow label="Weight Class" value={formData.weightClass || '—'} />
                      <SRow label="Site" value={siteName} />
                      {clientRequiresVehicleCategory && vehicleCategory && (
                        <SRow label="Vehicle Category" value={vehicleCategory} />
                      )}
                      <SRow label="Modifications"
                        value={formData.hasModifications === 'yes'
                          ? <span style={{ color: '#b45309', fontWeight: 600 }}>Yes — {formData.modificationDetails}</span>
                          : 'No'}
                      />
                      <SRow label="Vehicle Ownership" value={ownerLabel} />
                      <SRow label="Declared Value"
                        value={
                          <span style={{ fontWeight: 600, color: '#166534' }}>
                            {vPreview ? `${vPreview.monetary} (${vPreview.words})` : '—'}
                          </span>
                        }
                      />
                      <SRow label="Cover Start Date" value={formData.inceptionDate ? formatDateBritish(formData.inceptionDate) : '—'} />
                      {addVehicleStatusRemoved === 'yes' && addVehicleStatusVehicleId && (() => {
                        const removedV = vehicles.find(v => v.id === parseInt(addVehicleStatusVehicleId));
                        return <SRow label="Vehicle Removed" value={removedV ? `${removedV.registration} — ${removedV.make} ${removedV.model}` : '—'} />;
                      })()}
                    </div>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, cursor: 'pointer', padding: '12px 14px', background: '#fafafa', border: '1.5px solid #d1d5db', borderRadius: 8 }}>
                      <input
                        type="checkbox"
                        checked={permTcConfirmed}
                        onChange={e => setPermTcConfirmed(e.target.checked)}
                        style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0 }}
                      />
                      <span style={{ lineHeight: 1.6 }}>
                        I confirm that I have read and understood your{' '}
                        <a href="https://www.yourshield.co.uk/terms-of-business/" target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8' }}>terms &amp; conditions</a>
                        {' '}and{' '}
                        <a href="https://www.yourshield.co.uk/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8' }}>privacy notice</a>.
                      </span>
                    </label>
                  </div>
                );
              })()}

              <div className="form-actions">
                {showPermVehicleSummary && !editingVehicle && !isAdmin ? (
                  <>
                    <button type="button" className="btn btn-secondary"
                      onClick={() => { setShowPermVehicleSummary(false); setPermTcConfirmed(false); }}>
                      ← Back
                    </button>
                    <button type="submit" className="btn btn-primary"
                      disabled={!permTcConfirmed || submitting}>
                      ✓ Confirm &amp; Submit Vehicle
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="btn btn-secondary" onClick={resetForm}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {editingVehicle ? 'Update Vehicle' : 'Review & Submit'}
                    </button>
                  </>
                )}
              </div>
            </form>
            {submitting && (
              <div className="vehicle-processing-overlay">
                <div className="vehicle-processing-box">
                  <img
                    src={clientVehicleImage || plainVanImg}
                    alt="Processing"
                    className="vehicle-processing-van"
                    style={{ width: '110px', height: '110px', objectFit: 'contain' }}
                  />
                  <p className="vehicle-processing-label">Processing…</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {vehicles.length > 0 && (
        <div style={{ marginBottom: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Search */}
          <input
            type="text"
            placeholder="🔍 Search by reg, make, model..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            style={{ flex: '2 1 200px', padding: '10px 14px', fontSize: '14px', borderRadius: '8px', border: '2px solid #e2e8f0', outline: 'none' }}
            onFocus={(e) => e.target.style.borderColor = '#4299e1'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
          {/* Cover status filter */}
          <select
            value={filterCoverStatus}
            onChange={(e) => setFilterCoverStatus(e.target.value)}
            style={{ flex: '1 1 150px', padding: '10px 12px', fontSize: '14px', borderRadius: '8px', border: '2px solid #e2e8f0', background: '#fff' }}
          >
            <option value="">All Statuses</option>
                            <option value="Active">On Cover</option>
                            <option value="AwaitingInception">Awaiting Inception</option>
                            <option value="Cover Pending">Cover Pending</option>
                            <option value="Pending Removal">Pending Removal</option>
          </select>
          {/* Cover type filter */}
          <select
            value={filterCoverType}
            onChange={(e) => setFilterCoverType(e.target.value)}
            style={{ flex: '1 1 130px', padding: '10px 12px', fontSize: '14px', borderRadius: '8px', border: '2px solid #e2e8f0', background: '#fff' }}
          >
            <option value="">Perm &amp; Temp</option>
            <option value="Permanent">Permanent Only</option>
            <option value="Temporary">Temporary Only</option>
          </select>
          {/* Site filter */}
          {sites.filter(s => isAdmin ? s.clientId === viewClientId : s.clientId === user?.clientId).length > 0 && (
            <select
              value={filterSiteId}
              onChange={(e) => setFilterSiteId(e.target.value)}
              style={{ flex: '1 1 160px', padding: '10px 12px', fontSize: '14px', borderRadius: '8px', border: '2px solid #e2e8f0', background: '#fff' }}
            >
              <option value="">All Sites</option>
              <option value="-1">Head Office</option>
              {sites
                .filter(s => isAdmin ? s.clientId === viewClientId : s.clientId === user?.clientId)
                .map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)
              }
            </select>
          )}
          {/* A–Z sort toggle */}
          <button
            type="button"
            onClick={() => setSortRegAZ(v => !v)}
            style={{
              padding: '10px 14px', fontSize: '13px', borderRadius: '8px', border: '2px solid #e2e8f0',
              background: sortRegAZ ? '#002060' : '#fff', color: sortRegAZ ? '#fff' : '#374151',
              cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: sortRegAZ ? 700 : 400,
            }}
          >
            Reg A–Z {sortRegAZ ? '↓' : ''}
          </button>
          {/* Download current fleet */}
          {!isAdmin && (
            <button
              type="button"
              onClick={handleDownloadCurrentFleet}
              style={{
                padding: '10px 14px', fontSize: '13px', borderRadius: '8px',
                border: '2px solid #10b981', background: '#ecfdf5', color: '#065f46',
                cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600,
              }}
              title="Download the vehicles currently shown on screen"
            >
              📥 Download Fleet
            </button>
          )}
        </div>
      )}

      <div className="card">
        {(() => {
          let filteredVehicles = vehicles.filter(v => {
            if (searchFilter) {
              const s = searchFilter.toLowerCase();
              if (!(
                v.registration.toLowerCase().includes(s) ||
                v.make.toLowerCase().includes(s) ||
                v.model.toLowerCase().includes(s) ||
                v.client?.companyName?.toLowerCase().includes(s)
              )) return false;
            }
            if (filterCoverStatus) {
              if (filterCoverStatus === 'AwaitingInception') {
                // Pseudo-status: Active vehicles whose inception date is still in the future
                const isAwaiting = v.coverStatus === 'Active' &&
                  v.inceptionDate != null &&
                  new Date(v.inceptionDate).setHours(0,0,0,0) > new Date().setHours(0,0,0,0);
                if (!isAwaiting) return false;
              } else if (filterCoverStatus === 'Active') {
                // On Cover = Active BUT not future-dated
                const isFuture = v.inceptionDate != null &&
                  new Date(v.inceptionDate).setHours(0,0,0,0) > new Date().setHours(0,0,0,0);
                if (v.coverStatus !== 'Active' || isFuture) return false;
              } else {
                if (v.coverStatus !== filterCoverStatus) return false;
              }
            }
            if (filterSiteId) {
              if (filterSiteId === '-1' && v.siteId != null) return false;
              if (filterSiteId !== '-1' && String(v.siteId) !== filterSiteId) return false;
            }
            if (filterCoverType === 'Permanent' && v.tempCoverInfo) return false;
            if (filterCoverType === 'Temporary' && !v.tempCoverInfo) return false;
            return true;
          });
          if (sortRegAZ) {
            filteredVehicles = [...filteredVehicles].sort((a, b) => a.registration.localeCompare(b.registration));
          }

          // Client users with assigned sites only see vehicles at their site(s)
          if (userSiteIds.length > 0) {
            filteredVehicles = filteredVehicles.filter(v => v.siteId != null && userSiteIds.includes(v.siteId));
          }

          if (filteredVehicles.length === 0) {
          if (isAdmin && viewClientId <= 0) {
              return <p className="empty-state">🏭 Please select a client from the dropdown above to view their vehicles</p>;
            }
            return searchFilter ? (
              <p className="empty-state">No vehicles found matching "{searchFilter}"</p>
            ) : (
              <p className="empty-state">No vehicles found. Add your first vehicle to get started.</p>
            );
          }

          return (
            <table>
              <thead>
                <tr>
                  {isAdmin && viewClientId > 0 && (
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={selectedVehicles.size === filteredVehicles.length && filteredVehicles.length > 0}
                        onChange={handleSelectAllVehicles}
                        title="Select all vehicles"
                      />
                    </th>
                  )}
                  <th>Registration</th>
                  {isAdmin && viewClientId === -1 && <th>Client</th>}
                  <th>Make</th>
                  <th style={{ minWidth: '180px' }}>Model</th>
                  <th style={{ width: '50px', textAlign: 'center' }}>Year</th>
                  <th>Cover Status</th>
                  <th>Site</th>
                  <th style={{ textAlign: 'center' }}>Value</th>
                  <th style={{ textAlign: 'center' }}>Policy Start</th>
                  <th style={{ textAlign: 'center' }}>Policy End</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>Days Cover Remaining / Type of Cover</th>
                  <th style={{ textAlign: 'center' }}>Added</th>
                  <th style={{ minWidth: '140px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVehicles.map((vehicle, idx) => {
                // Display site or Head Office with city
                const getLocationDisplay = () => {
                  if (vehicle.site) {
                    return vehicle.site.name;
                  }
                  // No site assigned - show Head Office with city
                  if (vehicle.client?.city) {
                    return `Head Office (${vehicle.client.city})`;
                  }
                  return 'Head Office';
                };
                
                // Future Dated: Active vehicle whose cover start date (inceptionDate) is still in the future
                const isFutureDated = vehicle.coverStatus === 'Active' &&
                  vehicle.inceptionDate != null &&
                  new Date(vehicle.inceptionDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0);

                const getCoverStatusColor = () => {
                  if (vehicle.coverStatus === 'Active') {
                    return isFutureDated ? '#7c3aed' : '#28a745';
                  }
                  if (vehicle.coverStatus === 'Pending Removal') return '#dc2626';
                  return '#FFA500';
                };

                const getCoverStatusText = () => {
                  if (vehicle.coverStatus === 'Active') {
                    return isFutureDated ? 'Awaiting Inception' : 'On Cover';
                  }
                  if (vehicle.coverStatus === 'Pending Removal') return 'Pending Removal';
                  return 'Cover Pending';
                };

                const colSpan = (isAdmin && viewClientId > 0 ? 1 : 0) +
                  (isAdmin && viewClientId === -1 ? 1 : 0) + 12;

                return (
                <>
                <tr key={vehicle.id}>
                  {isAdmin && viewClientId > 0 && (
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedVehicles.has(vehicle.id)}
                        onChange={() => handleToggleVehicleSelection(vehicle.id)}
                      />
                    </td>
                  )}
                  <td><strong>{vehicle.registration}</strong></td>
                  {isAdmin && viewClientId === -1 && (
                    <td>{vehicle.client?.companyName || 'Unknown'}</td>
                  )}
                  <td>{vehicle.make}</td>
                  <td>{vehicle.model}</td>
                  <td>{vehicle.year}</td>
                  <td>
                    <div>
                      <span style={{
                        color: getCoverStatusColor(),
                        fontWeight: '600',
                        fontSize: '13px',
                        ...(isFutureDated ? {
                          animation: 'futurePulse 1.8s ease-in-out infinite',
                        } : {})
                      }}>
                        {getCoverStatusText()}
                      </span>
                    </div>
                  </td>
                  <td>{getLocationDisplay()}</td>
                  <td style={{ textAlign: 'center' }}>£{vehicle.vehicleValue ? vehicle.vehicleValue.toLocaleString() : '0'}</td>
                  <td style={{ textAlign: 'center' }}>
                    {/* Policy Start — date normal size, time always 11px */}
                    {(() => {
                      const startSrc = vehicle.inceptionDate || vehicle.tempCoverInfo?.coverStartDate;
                      if (!startSrc) return <span>-</span>;
                      const londonDate = formatLondonDate(startSrc) || '-';
                      const londonTime = formatLondonTime(startSrc);
                      const timeStyle: React.CSSProperties = {
                        fontSize: '11px',
                        lineHeight: 1.3,
                        color: isFutureDated ? '#7c3aed' : '#6b7280',
                        fontWeight: isFutureDated ? 600 : 400,
                      };
                      return (
                        <>
                          <div style={isFutureDated ? {
                            color: '#7c3aed', fontWeight: 600,
                            animation: 'futurePulse 1.8s ease-in-out infinite'
                          } : undefined}>
                            {londonDate}
                          </div>
                          {londonTime ? <div style={timeStyle}>{londonTime}</div> : null}
                        </>
                      );
                    })()}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {/* Policy End — same layout: date normal, time 11px */}
                    {(() => {
                      const timeStyle: React.CSSProperties = {
                        fontSize: '11px',
                        lineHeight: 1.3,
                        color: '#6b7280',
                        fontWeight: 400,
                      };
                      if (vehicle.tempCoverInfo?.coverEndDate) {
                        return (
                          <>
                            <div>{formatLondonDate(vehicle.tempCoverInfo.coverEndDate) || formatDateBritish(vehicle.tempCoverInfo.coverEndDate)}</div>
                            <div style={timeStyle}>23:59 UK</div>
                          </>
                        );
                      }
                      const endSrc = clientPolicyEndDate || vehicle.coverEndDate;
                      if (!endSrc) return <span>-</span>;
                      return (
                        <>
                          <div>{formatLondonDate(endSrc) || formatDateBritish(endSrc)}</div>
                          <div style={timeStyle}>{formatLondonTime(endSrc) || '23:59 UK'}</div>
                        </>
                      );
                    })()}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {(() => {
                      // Days remaining: temp uses temp end; perm uses client policy end
                      const usesClientPolicyEnd = !vehicle.tempCoverInfo && !!clientPolicyEndDate;
                      const endStr = vehicle.tempCoverInfo
                        ? vehicle.tempCoverInfo.coverEndDate
                        : (clientPolicyEndDate || vehicle.coverEndDate);
                      if (!endStr) return '-';
                      const end = usesClientPolicyEnd ? parseApiTimestamp(endStr) : new Date(endStr);
                      // Temporary and legacy vehicle cover dates are date-only and end at
                      // 23:59. Client policy schedules are precise UTC date-times.
                      if (!usesClientPolicyEnd) end.setHours(23, 59, 0, 0);
                      const now = new Date();
                      const isFutureInception = !vehicle.tempCoverInfo && !!vehicle.inceptionDate &&
                        new Date(vehicle.inceptionDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0);
                      const countdownStart = isFutureInception ? new Date(vehicle.inceptionDate!) : now;
                      const days = Math.ceil((end.getTime() - countdownStart.getTime()) / (1000 * 60 * 60 * 24));
                      const daysDisplay = days > 0 ? days : 0;
                      return (
                        <div>
                          <span style={{
                            color: daysDisplay > 30 ? '#28a745' : daysDisplay > 7 ? '#FFA500' : '#dc3545',
                            fontWeight: '600',
                            fontSize: '12px',
                          }}>
                            {daysDisplay}
                            <span style={{ fontSize: '11px', fontWeight: '400', marginLeft: '2px' }}>days</span>
                          </span>
                          <div style={{ fontSize: '10px', marginTop: '2px' }}>
                            <span style={{
                              padding: '1px 5px', borderRadius: '9999px', fontWeight: 600,
                              backgroundColor: vehicle.tempCoverInfo ? '#dbeafe' : '#d1fae5',
                              color: vehicle.tempCoverInfo ? '#1e40af' : '#065f46',
                            }}>
                              {vehicle.tempCoverInfo ? 'Temp' : 'Perm'}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ textAlign: 'center' }}>{formatLondonDate(vehicle.createdAt) || formatDateBritish(vehicle.createdAt)}</td>
                  <td>
                    <div className="action-buttons" style={{ gap: '4px', flexWrap: 'nowrap' }}>
                      {isAdmin && (
                        <button className="btn-icon" onClick={() => handleEdit(vehicle)} title="Edit vehicle" style={{ fontSize: '14px', padding: '4px 6px' }}>
                          ✏️
                        </button>
                      )}
                      <button className="btn-icon" onClick={() => handleViewHistory(vehicle.id)} title="View vehicle history" style={{ fontSize: '14px', padding: '4px 6px' }}>
                        📋
                      </button>
                      {(user?.role === 'SuperAdmin' || user?.canSendVehicleNotifications) && (
                        <button className="btn-icon" onClick={() => handleSendVehicleNotification(vehicle)} title="Send vehicle notification email" style={{ fontSize: '14px', padding: '4px 6px' }}>
                          📧
                        </button>
                      )}
                      {isSuperAdmin && (
                        <button className="btn-icon" onClick={() => handleSilentRemove(vehicle)} title="Silent Remove — no client emails" style={{ fontSize: '14px', padding: '4px 6px', opacity: 0.75 }}>
                          🔇
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                </>
                );
              })}
                </tbody>
              </table>
            );
          })()}
        </div>

      {showHistoryModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '80vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>Vehicle History</h2>
              <button className="close-btn" onClick={() => setShowHistoryModal(false)}>✕</button>
            </div>

            {loadingHistory ? (
              <p style={{ padding: '20px', textAlign: 'center' }}>Loading history...</p>
            ) : historyData ? (
              <div style={{ padding: '20px' }}>
                <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' }}>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#2d3748' }}>
                    {historyData.vehicle.registration}
                  </h3>
                  <p style={{ margin: '0', color: '#718096', fontSize: '14px' }}>
                    {historyData.vehicle.make} {historyData.vehicle.model} ({historyData.vehicle.year})
                  </p>
                  <p style={{ margin: '8px 0 0 0', fontSize: '13px' }}>
                    <span style={{ fontWeight: '600', color: '#4a5568' }}>Current Status:</span>{' '}
                    <span style={{
                      color: historyData.vehicle.coverStatus === 'Active' ? '#28a745' : '#FFA500',
                      fontWeight: '600'
                    }}>
                      {historyData.vehicle.coverStatus === 'Active' ? 'On Cover' : 'Cover Pending'}
                    </span>
                    {historyData.vehicle.coverStatus === 'Cover Pending' && historyData.vehicle.coverEffectiveDate && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#718096' }}>
                        - Cover Incepts: {(() => {
                          const date = new Date(historyData.vehicle.coverEffectiveDate);
                          const day = String(date.getDate()).padStart(2, '0');
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const year = date.getFullYear();
                          return `${day}/${month}/${year}`;
                        })()}
                      </span>
                    )}
                  </p>
                </div>

                <div style={{ marginBottom: '24px', padding: '16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px' }}>
                  <label htmlFor="vehicle-history-note" style={{ display: 'block', marginBottom: '8px', color: '#991b1b', fontWeight: 700 }}>
                    Add custom history note
                  </label>
                  <textarea
                    id="vehicle-history-note"
                    value={historyNote}
                    onChange={(e) => setHistoryNote(e.target.value)}
                    maxLength={2000}
                    rows={3}
                    placeholder="Enter a note for this vehicle's audit history..."
                    disabled={savingHistoryNote}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '10px', border: '1px solid #fca5a5', borderRadius: '6px', resize: 'vertical', fontFamily: 'inherit' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                    <span style={{ color: '#b91c1c', fontSize: '12px' }}>{historyNote.length}/2000</span>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAddHistoryNote}
                      disabled={savingHistoryNote || !historyNote.trim()}
                      style={{ background: '#b91c1c', borderColor: '#b91c1c' }}
                    >
                      {savingHistoryNote ? 'Saving Note...' : 'Add Note to History'}
                    </button>
                  </div>
                </div>
                <h4 style={{ marginBottom: '16px', fontSize: '18px', color: '#2d3748' }}>Complete History</h4>
                
                {historyData.history.length === 0 ? (
                  <p style={{ color: '#718096', fontStyle: 'italic' }}>No history records found.</p>
                ) : (
                  <div style={{ position: 'relative' }}>
                    {/* Timeline line */}
                    <div style={{
                      position: 'absolute',
                      left: '15px',
                      top: '0',
                      bottom: '0',
                      width: '2px',
                      backgroundColor: '#e2e8f0'
                    }} />
                    
                    {historyData.history.map((entry: any, index: number) => {
                      const type = entry.type || entry.Type || '';
                      const action = entry.action || entry.Action || 'Unknown Action';
                      const timestamp = entry.timestamp || entry.Timestamp;
                      const description = entry.description || entry.Description || '';
                      const performedBy = entry.performedBy || entry.PerformedBy || 'Unknown';
                      const userCompany = entry.userCompany || entry.UserCompany || 'Unknown';
                      const userRole = entry.userRole || entry.UserRole || '';
                      const isCustomNote = action === 'CustomNote';
                      const isPolicyYear = type === 'PolicyYear'
                        || action === 'PolicyYearStart'
                        || /Policy Period \d+ Started/i.test(action);

                      // Dedupe: hide the change-log twin when PolicyYearStart audit exists
                      if (type === 'Change' && /Policy Period \d+ Started/i.test(action)) {
                        const hasAuditTwin = historyData.history.some((other: any) => {
                          const oa = other.action || other.Action || '';
                          const ot = other.timestamp || other.Timestamp;
                          return (oa === 'PolicyYearStart' || (other.type || other.Type) === 'PolicyYear')
                            && ot && timestamp
                            && Math.abs(new Date(ot).getTime() - new Date(timestamp).getTime()) < 60_000;
                        });
                        if (hasAuditTwin) return null;
                      }
                      
                      // Filter out "currentuser" from display
                      if (performedBy.toLowerCase() === 'currentuser') {
                        return null;
                      }

                      const displayAction = isPolicyYear
                        ? (action === 'PolicyYearStart' ? 'New policy year started' : action)
                        : action;
                      
                      return (
                        <div key={index} style={{
                          position: 'relative',
                          marginBottom: '20px',
                          paddingLeft: '45px'
                        }}>
                          {/* Timeline dot */}
                          <div style={{
                            position: 'absolute',
                            left: '8px',
                            top: '4px',
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: isCustomNote ? '#dc2626' :
                                            isPolicyYear ? '#5b21b6' :
                                            type === 'Approval' ? '#28a745' :
                                            type === 'Audit' ? '#4299e1' :
                                            type === 'Change' ? '#FFA500' :
                                            type === 'Reinstatement' ? '#9333ea' : '#dc3545',
                            border: '2px solid white',
                            boxShadow: '0 0 0 2px #e2e8f0'
                          }} />
                          
                          <div style={{
                            backgroundColor: isCustomNote ? '#fef2f2' : isPolicyYear ? '#f5f3ff' : '#f7fafc',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            border: isCustomNote ? '1px solid #fca5a5' : isPolicyYear ? '1px solid #c4b5fd' : '1px solid #e2e8f0'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                              <div style={{ flex: 1 }}>
                                <span style={{ fontWeight: '600', fontSize: '15px', color: isCustomNote ? '#b91c1c' : isPolicyYear ? '#5b21b6' : '#2d3748' }}>
                                  {isCustomNote ? `Custom note added by ${performedBy}` : isPolicyYear ? `📅 ${displayAction}` : displayAction}
                                </span>
                              </div>
                              <span style={{ fontSize: '13px', color: '#4a5568', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                                {timestamp ? (formatLondonDateTime(timestamp) || formatDateTimeBritish(timestamp)) : 'N/A'}
                              </span>
                            </div>
                            {description && (
                              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: isCustomNote ? '#991b1b' : isPolicyYear ? '#5b21b6' : '#4a5568' }}>
                                {description}
                              </p>
                            )}
                            {isCustomNote ? (
                              <p style={{ margin: '0', fontSize: '12px', color: '#b91c1c' }}>
                                Audit note recorded by <span style={{ fontWeight: '600' }}>{performedBy}</span>
                              </p>
                            ) : (
                              <p style={{ margin: '0', fontSize: '12px', color: '#718096' }}>
                                by <span style={{ fontWeight: '500' }}>{userCompany}</span>
                                {userRole && <span style={{ color: '#a0aec0' }}> ({userRole})</span>}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <p style={{ padding: '20px', textAlign: 'center', color: '#dc3545' }}>Failed to load history</p>
            )}
          </div>
        </div>
      )}

      {/* Temp Cover Modal */}
      {showTempCoverModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>Add Temporary Vehicle</h2>
              <button className="close-btn" onClick={closeTempCoverModal}>✕</button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              // First submission shows summary; second submits
              if (!showTempSummary) {
                setShowTempSummary(true);
                return;
              }
              setTempSubmitting(true);
              try {
                const vehicleData = {
                  registration: tempFormReg,
                  make: tempFormMake,
                  model: tempFormModel,
                  vehicleType: tempFormVehicleType,
                  weightClass: tempFormWeightClass,
                  grossVehicleWeightKg: tempGrossVehicleWeightKg,
                  year: tempFormYear,
                  vehicleValue: tempVehicleValue,
                  hasModifications: tempHasModifications === 'yes',
                  modificationDetails: tempHasModifications === 'yes' ? tempModificationDetails : null,
                  siteId: tempSiteIdRaw ? parseInt(tempSiteIdRaw) : undefined,
                  vehicleCategory: clientRequiresVehicleCategory ? (tempVehicleCategory || null) : null,
                  isTemporaryCover: true, // suppresses TWIMC from PostVehicle; correct one comes from RequestTempCover
                  registeredOwnerIsBusiness: tempBusinessIsOwnerAndKeeper === 'yes' ? true : tempBusinessIsOwnerAndKeeper === 'lease' ? null : tempBusinessIsOwnerAndKeeper === 'other' ? false : null,
                  registeredOwnerBusinessName: tempBusinessIsOwnerAndKeeper === 'yes' ? (clientDetails?.name || null) : null,
                  registeredOwnerAddress: tempBusinessIsOwnerAndKeeper === 'yes' ? (clientDetails?.address || null) : null,
                  registeredKeeperIsBusiness: tempBusinessIsOwnerAndKeeper === 'yes' ? true : tempBusinessIsOwnerAndKeeper === 'lease' ? true : tempBusinessIsOwnerAndKeeper === 'other' ? false : null,
                  registeredKeeperBusinessName: (tempBusinessIsOwnerAndKeeper === 'yes' || tempBusinessIsOwnerAndKeeper === 'lease') ? (clientDetails?.name || null) : null,
                  registeredKeeperAddress: (tempBusinessIsOwnerAndKeeper === 'yes' || tempBusinessIsOwnerAndKeeper === 'lease') ? (clientDetails?.address || null) : null,
                };
                const vehicleResponse = await vehiclesAPI.create(vehicleData);
                await workstreamAPI.requestTempCover(vehicleResponse.data.id, tempCoverStartDT, tempCoverEndDT);
                alert('Temporary vehicle added and cover request submitted successfully. Admin will review your request.');
                closeTempCoverModal();
                loadVehicles();
              } catch (error: any) {
                console.error('Error adding temp vehicle:', error);
                setShowTempSummary(false);
                alert(error.response?.data?.message || 'Failed to add temporary vehicle');
              } finally {
                setTempSubmitting(false);
              }
            }}>
              {/* ── Form fields — hidden during summary review ── */}
              {!showTempSummary && (
              <>
              <h3 style={{ marginTop: '0', marginBottom: '16px', fontSize: '18px', color: '#2d3748' }}>Vehicle Details</h3>

              <div className="form-group">
                <label style={{ textAlign: 'center', display: 'block' }}>Registration Number *</label>
                <div className="uk-plate-container">
                  <div className="uk-plate-wrapper">
                    <div className="uk-plate-strip">
                      <img src={shieldIcon} alt="" className="uk-plate-shield-icon" />
                      <span className="uk-plate-stars">★★★</span>
                      <span className="uk-plate-gb">GB</span>
                    </div>
                    <input
                      type="text"
                      className="uk-plate-input"
                      name="registration"
                      required
                      placeholder="AB12 CDE"
                      maxLength={8}
                      value={tempFormReg}
                      onChange={(e) => setTempFormReg(e.target.value.toUpperCase().replace(/[^A-Z0-9 ]/g, ''))}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleVrmLookup(tempFormReg, 'temp')}
                    disabled={vrmLookupLoading || !tempFormReg.trim()}
                  >
                    {vrmLookupLoading ? 'Searching...' : 'Search Registration'}
                  </button>
                </div>
              </div>

              {tempVrmLookupResult === 'found' && (
                <div className="vrm-result-banner vrm-result-banner--found" style={{ marginBottom: 16, flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <span>✓ Vehicle data verified by UKVD — is this the correct vehicle?</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="vrm-confirm-btn vrm-confirm-btn--yes" onClick={() => setTempVrmLookupResult('confirmed')}>✓ Yes, correct</button>
                    <button type="button" className="vrm-confirm-btn vrm-confirm-btn--no" onClick={() => {
                      setTempVrmLookupResult('rejected');
                      setTempFormMake(''); setTempFormModel(''); setTempFormYear('');
                      setTempFormVehicleType(''); setTempFormWeightClass('');
                    }}>✗ No, enter manually</button>
                  </div>
                </div>
              )}
              {tempVrmLookupResult === 'confirmed' && (
                <div className="vrm-result-banner vrm-result-banner--found" style={{ marginBottom: 16 }}>
                  ✓ Confirmed — vehicle details locked
                </div>
              )}
              {tempVrmLookupResult === 'rejected' && (
                <div className="vrm-result-banner vrm-result-banner--rejected" style={{ marginBottom: 16 }}>
                  ✗ Please enter the vehicle details manually below
                </div>
              )}
              {tempVrmLookupResult === 'notfound' && (
                <div className="vrm-result-banner vrm-result-banner--notfound" style={{ marginBottom: 16 }}>
                  ⚠ Vehicle not found — please enter the details below manually
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Make *</label>
                  <input
                    type="text"
                    name="make"
                    required
                    placeholder="e.g. Ford"
                    readOnly={tempVrmLookupResult === 'found' || tempVrmLookupResult === 'confirmed'}
                    className={(tempVrmLookupResult === 'found' || tempVrmLookupResult === 'confirmed') ? 'vrm-locked-field' : undefined}
                    value={tempFormMake}
                    onChange={(e) => { if (tempVrmLookupResult !== 'found') setTempFormMake(e.target.value); }}
                  />
                </div>

                <div className="form-group">
                  <label>Model *</label>
                  <input
                    type="text"
                    name="model"
                    required
                    placeholder="e.g. Focus"
                    readOnly={tempVrmLookupResult === 'found' || tempVrmLookupResult === 'confirmed'}
                    className={(tempVrmLookupResult === 'found' || tempVrmLookupResult === 'confirmed') ? 'vrm-locked-field' : undefined}
                    value={tempFormModel}
                    onChange={(e) => { if (tempVrmLookupResult !== 'found') setTempFormModel(e.target.value); }}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Vehicle Type *</label>
                  <select name="vehicleType" required className={(tempVrmLookupResult === 'found' || tempVrmLookupResult === 'confirmed') ? 'vrm-locked-field' : undefined} value={tempFormVehicleType} onChange={(e) => { if (tempVrmLookupResult !== 'found') setTempFormVehicleType(e.target.value); }}>
                    <option value="">Select vehicle type...</option>
                    {tempFormVehicleType.includes(' (') && (
                      <option value={tempFormVehicleType}>{tempFormVehicleType}</option>
                    )}
                    <option value="Car">Car</option>
                    <option value="Van">Van</option>
                    <option value="Truck">Truck</option>
                    <option value="Bus">Bus</option>
                    <option value="Special Type">Special Type</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Weight Class *</label>
                  <select name="weightClass" required className={(tempVrmLookupResult === 'found' || tempVrmLookupResult === 'confirmed') ? 'vrm-locked-field' : undefined} value={tempFormWeightClass} onChange={(e) => { if (tempVrmLookupResult !== 'found') setTempFormWeightClass(e.target.value); }}>
                    <option value="">Select weight class...</option>
                    {tempFormWeightClass.includes(' (') && (
                      <option value={tempFormWeightClass}>{tempFormWeightClass}</option>
                    )}
                    <option value="Car">Car</option>
                    <option value="Up to 3.5T">Up to 3.5T</option>
                    <option value="Up to 7.5T">Up to 7.5T</option>
                    <option value="Up to 18T">Up to 18T</option>
                    <option value="Up to 26T">Up to 26T</option>
                    <option value="Up to 40T">Up to 40T</option>
                    <option value="More than 40T">More than 40T</option>
                    <option value="Up to ABI 10">Up to ABI 10</option>
                    <option value="Up to ABI 20">Up to ABI 20</option>
                    <option value="Up to ABI 30">Up to ABI 30</option>
                    <option value="Up to ABI 40">Up to ABI 40</option>
                    <option value="Up to ABI 50">Up to ABI 50</option>
                    <option value="Non Rated ABI">Non Rated ABI</option>
                    <option value="Up to 16 Seater">Up to 16 Seater</option>
                    <option value="Up to 29 Seater">Up to 29 Seater</option>
                    <option value="Up to 53 Seater">Up to 53 Seater</option>
                    <option value="More than 53 Seater">More than 53 Seater</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Year *</label>
                  <input
                    type="text"
                    name="year"
                    required
                    placeholder="e.g. 2024"
                    maxLength={4}
                    readOnly={tempVrmLookupResult === 'found' || tempVrmLookupResult === 'confirmed'}
                    className={(tempVrmLookupResult === 'found' || tempVrmLookupResult === 'confirmed') ? 'vrm-locked-field' : undefined}
                    value={tempFormYear}
                    onChange={(e) => { if (tempVrmLookupResult !== 'found') setTempFormYear(e.target.value); }}
                  />
                </div>

                <div className="form-group">
                  <label>Site/Location *</label>
                  {(() => {
                    const clientSites = sites
                      .filter(s => isAdmin ? s.clientId === viewClientId : s.clientId === user?.clientId)
                      .slice()
                      .sort((a, b) => a.id - b.id);
                    return (
                      <>
                        <select
                          name="siteId"
                          value={tempSiteIdRaw}
                          onChange={(e) => setTempSiteIdRaw(e.target.value)}
                        >
                          {clientSites.length === 0
                            ? <option value="">&mdash; No sites configured &mdash;</option>
                            : clientSites.map(site => (
                                <option key={site.id} value={site.id}>{site.name}</option>
                              ))
                          }
                        </select>
                        <small style={{ color: '#718096', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          {clientSites.length === 0
                            ? 'No sites configured for this client \u2014 contact your administrator.'
                            : `${clientSites.length} site${clientSites.length !== 1 ? 's' : ''} available \u2014 defaulting to the first site added.`}
                        </small>
                      </>
                    );
                  })()}
                </div>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                  Declared Value (£) *
                  <InfoTooltip
                    label="Help with valuing the vehicle?"
                    content={<div>
                      <p style={{ margin: '0 0 8px' }}>The Declared Value should reflect the vehicle’s <strong>current market value</strong>, taking into account age, mileage, condition and any modifications.</p>
                      <p style={{ margin: 0, fontSize: 12 }}>In the event of a total loss, the insurer uses market value at the time of the incident.</p>
                    </div>}
                  />
                </label>
                <input
                  type="text"
                  name="vehicleValue"
                  inputMode="numeric"
                  required
                  placeholder="e.g. 15000"
                  value={tempVehicleValue}
                  onChange={(e) => setTempVehicleValue(e.target.value.replace(/[^0-9]/g, ''))}
                />
                {(() => { const p = vehicleValuePreview(tempVehicleValue); return p ? (
                  <div style={{ marginTop: '7px', padding: '8px 12px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', fontSize: '13px', color: '#0c4a6e', fontWeight: 500 }}>
                    {p.monetary} &mdash; {p.words}
                  </div>
                ) : null; })()}
                {clientDetails?.mtaValueThreshold != null && (() => {
                  const v = parseInt(tempVehicleValue || '0', 10);
                  return v >= (clientDetails!.mtaValueThreshold as number) ? (
                    <div style={{ marginTop: 7, padding: '9px 12px', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 6, fontSize: 13, color: '#92400e', fontWeight: 600 }}>
                      ⚠️ Declared values of £{(clientDetails!.mtaValueThreshold as number).toLocaleString()} or above will be referred to the MTA team for review before cover is confirmed.
                    </div>
                  ) : null;
                })()}
                <small style={{ color: '#718096', fontSize: '12px', marginTop: '6px', display: 'block' }}>
                  All claims are settled at vehicle market value set by the insurer at time of incident
                </small>
                <small style={{ color: '#92400e', fontSize: '12px', marginTop: '3px', display: 'block', fontWeight: 700 }}>
                  ** If vehicle value is materially incorrect, cover may be invalidated.
                </small>
              </div>

              {/* Vehicle Category — only shown for clients with requiresVehicleCategory enabled */}
              {clientRequiresVehicleCategory && !isAdmin && (
                <div className="form-group" style={{ padding: '14px 16px', background: '#f0f9ff', border: '2px solid #bae6fd', borderRadius: '8px', marginBottom: '16px' }}>
                  <label style={{ fontWeight: 700, color: '#0369a1' }}>Vehicle Category *</label>
                  <select
                    required
                    value={tempVehicleCategory}
                    onChange={(e) => setTempVehicleCategory(e.target.value)}
                    style={{ marginTop: 6 }}
                  >
                    <option value="">Select category...</option>
                    <option value="TAV">TAV — Temporary Additional Vehicle</option>
                    <option value="Contract Hire">Contract Hire</option>
                  </select>
                  <small style={{ color: '#0369a1', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Your account requires a vehicle category to be selected for all submissions.
                  </small>
                </div>
              )}

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                  Does the vehicle have any modifications? *
                  <InfoTooltip
                    label="What are modifications?"
                    content={<div>
                      <p style={{ margin: '0 0 8px' }}>Modifications are any changes made <strong>after manufacture</strong>, including engine upgrades, body kits, suspension changes, custom paintwork, aftermarket exhausts, etc.</p>
                      <p style={{ margin: '0 0 6px', fontSize: 12, color: '#94a3b8' }}>This list is not exhaustive — any non-standard changes to the vehicle should be declared, regardless of whether they appear above.</p>
                      <p style={{ margin: 0, fontSize: 12, color: '#fca5a5' }}>⚠️ All modifications must be declared. Failure to disclose may result in a claim being declined or cover being void.</p>
                    </div>}
                  />
                </label>
                <select
                  name="hasModifications"
                  required
                  value={tempHasModifications}
                  onChange={(e) => setTempHasModifications(e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="no">N — No Modifications</option>
                  <option value="yes">Y — Has Modifications</option>
                </select>
              </div>

              {tempHasModifications === 'yes' && (
                <div className="form-group" style={{ 
                  backgroundColor: '#f7fafc', 
                  padding: '16px', 
                  borderRadius: '8px',
                  border: '2px solid #e2e8f0'
                }}>
                  <label>Modification Details *</label>
                  <textarea
                    name="modificationDetails"
                    required
                    value={tempModificationDetails}
                    onChange={(e) => setTempModificationDetails(e.target.value)}
                    placeholder="Please describe all modifications to the vehicle..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '14px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e0',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              )}

              {/* ── Owner / Keeper question for temp vehicle ── */}
              <div style={{ marginTop: 20, padding: '14px 16px', background: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: 8, marginBottom: 20 }}>
                <div className="form-group" style={{ marginBottom: 4 }}>
                  <label style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                    Vehicle Ownership *
                    <InfoTooltip
                      label="Vehicle Ownership Explained"
                      content={<div>
                        <p style={{ margin: '0 0 7px', fontWeight: 600 }}>Registered Keeper</p>
                        <p style={{ margin: '0 0 10px' }}>The person/company on the V5C (logbook) responsible for licensing the vehicle.</p>
                        <p style={{ margin: '0 0 7px', fontWeight: 600 }}>Leased / Hired for Cover Duration</p>
                        <p style={{ margin: '0 0 10px' }}>Where the vehicle is hired/leased for the full duration of requested temporary cover and ownership stays with another party.</p>
                        <p style={{ margin: 0, fontSize: 12, color: '#fca5a5' }}>Other arrangements must be declared and will be referred for review.</p>
                      </div>}
                    />
                  </label>
                  <select
                    required
                    value={tempBusinessIsOwnerAndKeeper}
                    onChange={(e) => {
                      const val = e.target.value as '' | 'yes' | 'lease' | 'other';
                      setTempBusinessIsOwnerAndKeeper(val);
                      setTempBusinessIsOwnerOtherDetails('');
                    }}
                  >
                    <option value="">Select...</option>
                    <option value="yes">{clientDetails?.name ? `Yes, ${clientDetails.name} is the registered keeper or owner` : 'Yes, we are the registered keeper or owner'}</option>
                    <option value="lease">No, the vehicle is leased or hired for the duration of the requested cover</option>
                    <option value="other">Other (please specify)</option>
                  </select>
                </div>
                {tempBusinessIsOwnerAndKeeper === 'other' && (
                  <div className="form-group" style={{ marginTop: 10, marginBottom: 0 }}>
                    <label style={{ fontSize: 13 }}>Please describe the ownership arrangement *</label>
                    <textarea
                      required
                      rows={2}
                      value={tempBusinessIsOwnerOtherDetails}
                      onChange={e => setTempBusinessIsOwnerOtherDetails(e.target.value)}
                      placeholder="e.g. Vehicle is on a short-term hire, owned by an individual, etc."
                      style={{ width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #cbd5e0', resize: 'vertical', fontFamily: 'inherit' }}
                    />
                    <div style={{ marginTop: 8, padding: '10px 14px', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 6, fontSize: 13, color: '#92400e' }}>
                      ⚠️ This vehicle will be referred to your Myshield handler for review before cover is confirmed.
                    </div>
                  </div>
                )}
              </div>

              <h3 style={{ marginTop: '24px', marginBottom: '16px', fontSize: '18px', color: '#2d3748' }}>Temporary Cover Period</h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Cover Start Date &amp; Time *</label>
                  <input
                    type="datetime-local"
                    name="coverStartDate"
                    required
                    min={new Date().toISOString().slice(0, 16)}
                    value={tempCoverStartDT}
                    onChange={(e) => setTempCoverStartDT(e.target.value)}
                  />
                  <small style={{color: '#718096', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                    Maximum 30 days cover period
                  </small>
                </div>

                <div className="form-group">
                  <label>Cover End Date &amp; Time *</label>
                  <input
                    type="datetime-local"
                    name="coverEndDate"
                    required
                    value={tempCoverEndDT}
                    onChange={(e) => setTempCoverEndDT(e.target.value)}
                    min={tempCoverStartDT || new Date().toISOString().slice(0, 16)}
                    max={tempCoverStartDT ? (() => {
                      const d = new Date(tempCoverStartDT);
                      d.setDate(d.getDate() + 30);
                      return d.toISOString().slice(0, 16);
                    })() : undefined}
                  />
                  {tempCoverStartDT && (
                    <small style={{color: '#92400e', fontSize: '12px', marginTop: '4px', display: 'block', fontWeight: 600}}>
                      Must be within 30 days of start date
                    </small>
                  )}
                </div>
              </div>
              </>)}

              {/* ── Summary step — shown after first submit click ── */}
              {showTempSummary && (() => {
                const ownerLabel =
                  tempBusinessIsOwnerAndKeeper === 'yes' ? (clientDetails?.name ? `Yes, ${clientDetails.name} is the registered keeper or owner` : 'Yes, we are the registered keeper or owner') :
                  tempBusinessIsOwnerAndKeeper === 'lease' ? 'No, leased / hired for duration of cover' :
                  tempBusinessIsOwnerAndKeeper === 'other' ? `Other — ${tempBusinessIsOwnerOtherDetails}` : '—';
                const vPreview = vehicleValuePreview(tempVehicleValue);
                const siteName = tempSiteIdRaw
                  ? (sites.find(s => s.id === parseInt(tempSiteIdRaw))?.name || `Site #${tempSiteIdRaw}`)
                  : 'Head Office (Default)';
                const SRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
                  <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9', padding: '7px 0', fontSize: 14 }}>
                    <span style={{ width: '45%', color: '#64748b', fontWeight: 600, flexShrink: 0 }}>{label}</span>
                    <span style={{ color: '#1e293b' }}>{value}</span>
                  </div>
                );
                return (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                      <h4 style={{ margin: '0 0 12px', fontSize: 15, color: '#0c4a6e', fontWeight: 700 }}>
                        📋 Please review your temporary vehicle submission
                      </h4>
                      <SRow label="Registration" value={<strong>{tempFormReg}</strong>} />
                      <SRow label="Make / Model" value={`${tempFormMake} ${tempFormModel} (${tempFormYear})`} />
                      <SRow label="Vehicle Type" value={tempFormVehicleType || '—'} />
                      <SRow label="Weight Class" value={tempFormWeightClass || '—'} />
                      <SRow label="Site" value={siteName} />
                      {clientRequiresVehicleCategory && tempVehicleCategory && (
                        <SRow label="Vehicle Category" value={tempVehicleCategory} />
                      )}
                      <SRow label="Modifications"
                        value={tempHasModifications === 'yes'
                          ? <span style={{ color: '#b45309', fontWeight: 600 }}>Yes — {tempModificationDetails}</span>
                          : 'No'}
                      />
                      <SRow label="Vehicle Ownership" value={ownerLabel} />
                      <SRow label="Declared Value" value={
                        <span style={{ fontWeight: 600, color: '#166534' }}>
                          {vPreview ? `${vPreview.monetary} (${vPreview.words})` : '—'}
                        </span>
                      } />
                      <SRow label="Cover Start" value={tempCoverStartDT ? formatDateTimeBritish(tempCoverStartDT) : '—'} />
                      <SRow label="Cover End" value={tempCoverEndDT ? formatDateTimeBritish(tempCoverEndDT) : '—'} />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, cursor: 'pointer', padding: '12px 14px', background: '#fafafa', border: '1.5px solid #d1d5db', borderRadius: 8 }}>
                      <input
                        type="checkbox"
                        checked={tempTcConfirmed}
                        onChange={e => setTempTcConfirmed(e.target.checked)}
                        style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0 }}
                      />
                      <span style={{ lineHeight: 1.6 }}>
                        I confirm all details above are correct and I have read and understood your{' '}
                        <a href="https://www.yourshield.co.uk/terms-of-business/" target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8' }}>terms &amp; conditions</a>
                        {' '}and{' '}
                        <a href="https://www.yourshield.co.uk/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8' }}>privacy notice</a>.
                      </span>
                    </label>
                  </div>
                );
              })()}

              <div className="form-actions">
                {showTempSummary ? (
                  <>
                    <button type="button" className="btn btn-secondary"
                      onClick={() => { setShowTempSummary(false); setTempTcConfirmed(false); }}>
                      ← Back
                    </button>
                    <button type="submit" className="btn btn-primary"
                      disabled={!tempTcConfirmed || tempSubmitting}>
                      ✓ Confirm &amp; Submit
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="btn btn-secondary" onClick={closeTempCoverModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={tempSubmitting}>
                      Review &amp; Submit
                    </button>
                  </>
                )}
              </div>
            </form>
            {tempSubmitting && (
              <div className="vehicle-processing-overlay">
                <div className="vehicle-processing-box">
                  <img
                    src={clientVehicleImage || plainVanImg}
                    alt="Processing"
                    className="vehicle-processing-van"
                    style={{ width: '110px', height: '110px', objectFit: 'contain' }}
                  />
                  <p className="vehicle-processing-label">Processing…</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Change of Site Modal */}
      {showChangeSiteModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
            <div className="modal-header">
              <h2>Change of Site</h2>
              <button className="close-btn" onClick={() => { setShowChangeSiteModal(false); setChangeSiteVehicleId(0); setChangeSiteNewSiteId(0); setChangeSiteNotes(''); setChangeSiteVehicleSearch(''); setShowChangeSiteConfirm(false); }}>✕</button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!changeSiteVehicleId || !changeSiteNewSiteId) {
                alert('Please select a vehicle and a new site.');
                return;
              }
              if (!changeSiteNotes.trim()) {
                alert('Please provide a reason for the site change.');
                return;
              }
              if (!showChangeSiteConfirm) {
                setShowChangeSiteConfirm(true);
                return;
              }
              setChangeSiteSubmitting(true);
              try {
                await workstreamAPI.changeSite(changeSiteVehicleId, changeSiteNewSiteId, changeSiteNotes || undefined);
                alert('Site changed successfully.');
                setShowChangeSiteModal(false);
                setChangeSiteVehicleId(0);
                setChangeSiteNewSiteId(0);
                setChangeSiteNotes('');
                setChangeSiteVehicleSearch('');
                setShowChangeSiteConfirm(false);
                loadVehicles();
              } catch (error: any) {
                alert(error.response?.data?.message || 'Failed to change site.');
              } finally {
                setChangeSiteSubmitting(false);
              }
            }}>

              {showChangeSiteConfirm ? (
                <>
                  {/* ── Confirmation summary ── */}
                  {(() => {
                    const selectedVehicle = vehicles.find(v => v.id === changeSiteVehicleId);
                    const currentSiteName = selectedVehicle?.site?.name ?? 'Head Office';
                    const newSiteName = sites.find(s => s.id === changeSiteNewSiteId)?.name ?? 'Unknown';
                    return (
                      <div style={{ padding: '16px', background: '#fefce8', border: '1.5px solid #fde68a', borderRadius: 8, marginBottom: 16, fontSize: 14, color: '#713f12', lineHeight: 1.6 }}>
                        <div style={{ fontWeight: 700, marginBottom: 10, fontSize: 15 }}>⚠️ Please confirm this site change:</div>
                        <div><strong>Vehicle:</strong> {selectedVehicle?.registration} — {selectedVehicle?.make} {selectedVehicle?.model}</div>
                        <div><strong>Current Site:</strong> {currentSiteName}</div>
                        <div><strong>New Site:</strong> {newSiteName}</div>
                        <div><strong>Reason:</strong> {changeSiteNotes}</div>
                      </div>
                    );
                  })()}
                  <div className="form-actions" style={{ marginTop: 16 }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowChangeSiteConfirm(false)} disabled={changeSiteSubmitting}>
                      ← Back
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={changeSiteSubmitting}>
                      {changeSiteSubmitting ? 'Updating…' : '✓ Confirm'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* ── Registration search filter ── */}
                  <div className="form-group">
                    <label style={{ fontWeight: 700 }}>Select Vehicle *</label>
                    <input
                      type="text"
                      placeholder="🔍 Type any part of the reg, e.g. last 3 letters…"
                      value={changeSiteVehicleSearch}
                      onChange={e => setChangeSiteVehicleSearch(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', marginBottom: 8, border: '1.5px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    />
                    <select
                      value={changeSiteVehicleId || ''}
                      onChange={(e) => { setChangeSiteVehicleId(parseInt(e.target.value) || 0); setChangeSiteNewSiteId(0); }}
                      required
                    >
                      <option value="">Select a vehicle…</option>
                      {vehicles
                        .filter(v => v.coverStatus !== 'Inactive' && v.coverStatus !== 'Pending Removal')
                        .filter(v => !changeSiteVehicleSearch || v.registration.replace(/\s/g, '').toLowerCase().includes(changeSiteVehicleSearch.replace(/\s/g, '').toLowerCase()))
                        .map(v => <option key={v.id} value={v.id}>{v.registration} — {v.make} {v.model}</option>)
                      }
                    </select>
                  </div>

                  {/* ── Site selector (shown once a vehicle is picked) ── */}
                  {changeSiteVehicleId > 0 && (
                    <div className="form-group">
                      <label style={{ fontWeight: 700, color: '#002060' }}>Move to Site *</label>
                      <select
                        value={changeSiteNewSiteId || ''}
                        onChange={(e) => setChangeSiteNewSiteId(parseInt(e.target.value) || 0)}
                        required
                        style={{ border: '2px solid #002060' }}
                      >
                        <option value="">Select destination site…</option>
                        {sites
                          .filter(s => s.id !== (vehicles.find(v => v.id === changeSiteVehicleId)?.siteId ?? -1))
                          .map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))
                        }
                      </select>
                    </div>
                  )}

                  {/* ── Move summary ── */}
                  {changeSiteVehicleId > 0 && changeSiteNewSiteId > 0 && (() => {
                    const selectedVehicle = vehicles.find(v => v.id === changeSiteVehicleId);
                    const currentSiteName = selectedVehicle?.site?.name ?? 'Head Office';
                    const newSiteName = sites.find(s => s.id === changeSiteNewSiteId)?.name ?? 'Unknown';
                    return (
                      <div style={{
                        padding: '12px 16px', background: '#f0f9ff',
                        border: '1px solid #bae6fd', borderRadius: 8, marginBottom: 12,
                        fontSize: 14, color: '#0c4a6e', lineHeight: 1.5
                      }}>
                        <strong>{selectedVehicle?.registration}</strong> ({selectedVehicle?.make} {selectedVehicle?.model}),
                        currently based at <strong>{currentSiteName}</strong>,
                        will move to <strong>{newSiteName}</strong>.
                      </div>
                    );
                  })()}

                  {/* ── Mandatory notes ── */}
                  {changeSiteVehicleId > 0 && changeSiteNewSiteId > 0 && (
                    <div className="form-group">
                      <label>Reason for Change *</label>
                      <textarea
                        value={changeSiteNotes}
                        onChange={(e) => setChangeSiteNotes(e.target.value)}
                        placeholder="Reason for site change…"
                        rows={3}
                        required
                        style={{ width: '100%', padding: '10px', fontSize: '14px', borderRadius: '6px', border: '1px solid #cbd5e0', resize: 'vertical', fontFamily: 'inherit' }}
                      />
                    </div>
                  )}

                  <div className="form-actions" style={{ marginTop: 16 }}>
                    <button type="button" className="btn btn-secondary" onClick={() => { setShowChangeSiteModal(false); setChangeSiteVehicleId(0); setChangeSiteNewSiteId(0); setChangeSiteNotes(''); setChangeSiteVehicleSearch(''); setShowChangeSiteConfirm(false); }} disabled={changeSiteSubmitting}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={changeSiteSubmitting || !changeSiteVehicleId || !changeSiteNewSiteId}>
                      Review &amp; Confirm
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      )}

      {/* ── Remove Vehicle Modal ──
                  <div className="uk-plate-wrapper">
                    <div className="uk-plate-strip">
                      <img src={shieldIcon} alt="" className="uk-plate-shield-icon" />
                      <span className="uk-plate-stars">★★★</span>
                      <span className="uk-plate-gb">GB</span>
                    </div>
                    <input
                      type="text"
                      className="uk-plate-input"
                      name="newRegistration"
                      required
                      placeholder="AB12 CDE"
                      maxLength={8}
                      value={changeFormReg}
                      onChange={(e) => setChangeFormReg(e.target.value.toUpperCase().replace(/[^A-Z0-9 ]/g, ''))}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleVrmLookup(changeFormReg, 'change')}
                    disabled={vrmLookupLoading || !changeFormReg.trim()}
                  >
                    {vrmLookupLoading ? 'Searching...' : 'Search Registration'}
                  </button>
                </div>
              </div>

              {changeVrmLookupResult === 'found' && (
                <div className="vrm-result-banner vrm-result-banner--found" style={{ marginBottom: 16, flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <span>✓ Vehicle data verified by UKVD — is this the correct vehicle?</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="vrm-confirm-btn vrm-confirm-btn--yes" onClick={() => setChangeVrmLookupResult('confirmed')}>✓ Yes, correct</button>
                    <button type="button" className="vrm-confirm-btn vrm-confirm-btn--no" onClick={() => {
                      setChangeVrmLookupResult('rejected');
                      setChangeFormMake(''); setChangeFormModel(''); setChangeFormYear('');
                      setChangeFormVehicleType(''); setChangeFormWeightClass('');
                    }}>✗ No, enter manually</button>
                  </div>
                </div>
              )}
              {changeVrmLookupResult === 'confirmed' && (
                <div className="vrm-result-banner vrm-result-banner--found" style={{ marginBottom: 16 }}>
                  ✓ Confirmed — vehicle details locked
                </div>
              )}
              {changeVrmLookupResult === 'rejected' && (
                <div className="vrm-result-banner vrm-result-banner--rejected" style={{ marginBottom: 16 }}>
                  ✗ Please enter the vehicle details manually below
                </div>
              )}
              {changeVrmLookupResult === 'notfound' && (
                <div className="vrm-result-banner vrm-result-banner--notfound" style={{ marginBottom: 16 }}>
                  ⚠ Vehicle not found — please enter the details below manually
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Make *</label>
                  <input
                    type="text"
                    name="newMake"
                    required
                    placeholder="e.g. Ford"
                    readOnly={changeVrmLookupResult === 'found' || changeVrmLookupResult === 'confirmed'}
                    className={(changeVrmLookupResult === 'found' || changeVrmLookupResult === 'confirmed') ? 'vrm-locked-field' : undefined}
                    value={changeFormMake}
                    onChange={(e) => { if (changeVrmLookupResult !== 'found') setChangeFormMake(e.target.value); }}
                  />
                </div>

                <div className="form-group">
                  <label>Model *</label>
                  <input
                    type="text"
                    name="newModel"
                    required
                    placeholder="e.g. Focus"
                    readOnly={changeVrmLookupResult === 'found' || changeVrmLookupResult === 'confirmed'}
                    className={(changeVrmLookupResult === 'found' || changeVrmLookupResult === 'confirmed') ? 'vrm-locked-field' : undefined}
                    value={changeFormModel}
                    onChange={(e) => { if (changeVrmLookupResult !== 'found') setChangeFormModel(e.target.value); }}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Vehicle Type *</label>
                  <select name="newVehicleType" required className={(changeVrmLookupResult === 'found' || changeVrmLookupResult === 'confirmed') ? 'vrm-locked-field' : undefined} value={changeFormVehicleType} onChange={(e) => { if (changeVrmLookupResult !== 'found') setChangeFormVehicleType(e.target.value); }}>
                    <option value="">Select vehicle type...</option>
                    {changeFormVehicleType.includes(' (') && (
                      <option value={changeFormVehicleType}>{changeFormVehicleType}</option>
                    )}
                    <option value="Car">Car</option>
                    <option value="Van">Van</option>
                    <option value="Truck">Truck</option>
                    <option value="Bus">Bus</option>
                    <option value="Special Type">Special Type</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Weight Class *</label>
                  <select name="newWeightClass" required className={(changeVrmLookupResult === 'found' || changeVrmLookupResult === 'confirmed') ? 'vrm-locked-field' : undefined} value={changeFormWeightClass} onChange={(e) => { if (changeVrmLookupResult !== 'found') setChangeFormWeightClass(e.target.value); }}>
                    <option value="">Select weight class...</option>
                    {changeFormWeightClass.includes(' (') && (
                      <option value={changeFormWeightClass}>{changeFormWeightClass}</option>
                    )}
                    <option value="Car">Car</option>
                    <option value="Up to 3.5T">Up to 3.5T</option>
                    <option value="Up to 7.5T">Up to 7.5T</option>
                    <option value="Up to 18T">Up to 18T</option>
                    <option value="Up to 26T">Up to 26T</option>
                    <option value="Up to 44T">Up to 44T</option>
                    <option value="More than 44T">More than 44T</option>
                    <option value="Up to ABI 10">Up to ABI 10</option>
                    <option value="Up to ABI 20">Up to ABI 20</option>
                    <option value="Up to ABI 30">Up to ABI 30</option>
                    <option value="Up to ABI 40">Up to ABI 40</option>
                    <option value="Up to ABI 50">Up to ABI 50</option>
                    <option value="Non Rated ABI">Non Rated ABI</option>
                    <option value="Up to 16 Seater">Up to 16 Seater</option>
                    <option value="Up to 29 Seater">Up to 29 Seater</option>
                    <option value="Up to 53 Seater">Up to 53 Seater</option>
                    <option value="More than 53 Seater">More than 53 Seater</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Year *</label>
                  <input
                    type="text"
                    name="newYear"
                    required
                    placeholder="e.g. 2024"
                    maxLength={4}
                    readOnly={changeVrmLookupResult === 'found' || changeVrmLookupResult === 'confirmed'}
                    className={(changeVrmLookupResult === 'found' || changeVrmLookupResult === 'confirmed') ? 'vrm-locked-field' : undefined}
                    value={changeFormYear}
                    onChange={(e) => { if (changeVrmLookupResult !== 'found') setChangeFormYear(e.target.value); }}
                  />
                </div>

                <div className="form-group">
                  <label>Vehicle Value (£) *</label>
                  <input
                    type="text"
                    name="newVehicleValue"
                    inputMode="numeric"
                    required
                    placeholder="e.g. 15000"
                    value={changeVehicleValue}
                    onChange={(e) => setChangeVehicleValue(e.target.value.replace(/[^0-9]/g, ''))}
                  />
                  {(() => { const p = vehicleValuePreview(changeVehicleValue); return p ? (
                    <div style={{ marginTop: '7px', padding: '8px 12px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', fontSize: '13px', color: '#0c4a6e', fontWeight: 500 }}>
                      {p.monetary} &mdash; {p.words}
                    </div>
                  ) : null; })()}
                  <small style={{ color: '#718096', fontSize: '12px', marginTop: '6px', display: 'block' }}>
                    All claims are settled at vehicle market value set by the insurer at time of incident
                  </small>
                  <small style={{ color: '#92400e', fontSize: '12px', marginTop: '3px', display: 'block', fontWeight: 700 }}>
                  ** If vehicle value is materially incorrect, cover may be invalidated.
                  </small>
                </div>
              </div>

              <div className="form-group">
                <label>Has Modifications? *</label>
                <select
                  name="newHasModifications"
                  required
                  onChange={(e) => {
                    const modSection = document.getElementById('changeModificationDetails');
                    if (modSection) {
                      modSection.style.display = e.target.value === 'yes' ? 'block' : 'none';
                    }
                  }}
                >
                  <option value="">Select...</option>
                  <option value="no">N - No Modifications</option>
                  <option value="yes">Y - Has Modifications</option>
                </select>
              </div>

              <div id="changeModificationDetails" style={{ display: 'none' }}>
                <div className="form-group" style={{ 
                  backgroundColor: '#f7fafc', 
                  padding: '16px', 
                  borderRadius: '8px',
                  border: '2px solid #e2e8f0'
                }}>
                  <label>Modification Details *</label>
                  <textarea
                    name="newModificationDetails"
                    placeholder="Please describe all modifications to the vehicle..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '14px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e0',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              </div>

                <div className="form-group">
                  <label>Cover Start Date *</label>
                  <input
                    type="date"
                    name="coverStartDate"
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <small style={{color: '#718096', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                    Cannot be backdated
                  </small>
                </div>
              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  name="requestNotes"
                  placeholder="Any additional information about this change of vehicle..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '14px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e0',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowChangeVehicleModal(false); setChangeVehicleValue(''); setChangeFormReg(''); setChangeFormMake(''); setChangeFormModel(''); setChangeFormYear(''); setChangeFormVehicleType(''); setChangeFormWeightClass(''); setChangeVrmLookupResult('none'); }} disabled={changeSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={changeSubmitting}>
                  Submit Request
                </button>
              </div>
            </form>
          </div>
              {changeSubmitting && (
              <div className="vehicle-processing-overlay">
              <div className="vehicle-processing-box">
                <img
                  src={clientVehicleImage || plainVanImg}
                  alt="Processing"
                  className="vehicle-processing-van"
                  style={{ width: '110px', height: '110px', objectFit: 'contain' }}
                />
                <p className="vehicle-processing-label">Processing…</p>
              </div>
            </div>
          )}
 */}
      {showRemoveVehicleModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Request Vehicle Removal from Cover</h2>
              <button className="close-btn" onClick={() => { setShowRemoveVehicleModal(false); setRemovalVehicleId(''); setRemovalCoverEndDate(''); setShowRemovalSummary(false); setRemovalTcConfirmed(false); }}>✕</button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!showRemovalSummary) {
                setShowRemovalSummary(true);
                return;
              }
              try {
                await workstreamAPI.requestRemoval(parseInt(removalVehicleId), undefined, removalCoverEndDate);
                alert('Removal request submitted successfully. Admin will review your request.');
                setShowRemoveVehicleModal(false);
                setRemovalVehicleId(''); setRemovalCoverEndDate('');
                setShowRemovalSummary(false); setRemovalTcConfirmed(false);
                loadVehicles();
              } catch (error: any) {
                console.error('Error requesting removal:', error);
                setShowRemovalSummary(false);
                alert(error.response?.data?.message || 'Failed to submit removal request');
              }
            }}>
              {!showRemovalSummary && (
                <>
                  <div className="form-group">
                    <label>Select Vehicle *</label>
                    <select
                      required
                      value={removalVehicleId}
                      onChange={(e) => setRemovalVehicleId(e.target.value)}
                    >
                      <option value="">Select a vehicle to remove...</option>
                      {vehicles.filter(v => v.isActive !== false && v.coverStatus !== 'Inactive').map((vehicle) => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.registration} - {vehicle.make} {vehicle.model}
                        </option>
                      ))}
                    </select>
                    <small style={{color: '#718096', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                      Only active vehicles can be removed from cover
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="removal-cover-end-date">Cover End Date &amp; Time *</label>
                    <input
                      id="removal-cover-end-date"
                      type="datetime-local"
                      required
                      value={removalCoverEndDate}
                      onChange={(e) => setRemovalCoverEndDate(e.target.value)}
                      min={canBackdateRemoval ? undefined : new Date().toISOString().slice(0, 16)}
                    />
                    <small style={{color: '#718096', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                      {canBackdateRemoval
                        ? 'Historical dates are allowed for authorised admin users and will be recorded in the audit trail.'
                        : 'Future date & time only'}
                    </small>
                  </div>
                </>
              )}

              {showRemovalSummary && (() => {
                const vehicle = vehicles.find(v => v.id === parseInt(removalVehicleId));
                const SRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
                  <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9', padding: '7px 0', fontSize: 14 }}>
                    <span style={{ width: '45%', color: '#64748b', fontWeight: 600, flexShrink: 0 }}>{label}</span>
                    <span style={{ color: '#1e293b' }}>{value}</span>
                  </div>
                );
                return (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                      <h4 style={{ margin: '0 0 12px', fontSize: 15, color: '#7f1d1d', fontWeight: 700 }}>
                        🛑 Please review your removal request
                      </h4>
                      <SRow label="Vehicle" value={
                        <strong>{vehicle ? `${vehicle.registration} — ${vehicle.make} ${vehicle.model}` : '—'}</strong>
                      } />
                      <SRow label="Cover Ends" value={removalCoverEndDate ? formatDateTimeBritish(removalCoverEndDate) : '—'} />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, cursor: 'pointer', padding: '12px 14px', background: '#fafafa', border: '1.5px solid #d1d5db', borderRadius: 8 }}>
                      <input
                        type="checkbox"
                        checked={removalTcConfirmed}
                        onChange={e => setRemovalTcConfirmed(e.target.checked)}
                        style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0 }}
                      />
                      <span style={{ lineHeight: 1.6 }}>
                        I confirm this removal request is correct. I understand that cover for this vehicle will end at the date and time shown above.
                      </span>
                    </label>
                  </div>
                );
              })()}

              <div className="form-actions">
                {showRemovalSummary ? (
                  <>
                    <button type="button" className="btn btn-secondary"
                      onClick={() => { setShowRemovalSummary(false); setRemovalTcConfirmed(false); }}>
                      ← Back
                    </button>
                    <button type="submit" className="btn btn-primary"
                      disabled={!removalTcConfirmed}>
                      ✓ Confirm Removal Request
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="btn btn-secondary" onClick={() => { setShowRemoveVehicleModal(false); setRemovalVehicleId(''); setRemovalCoverEndDate(''); setShowRemovalSummary(false); setRemovalTcConfirmed(false); }}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={!removalVehicleId || !removalCoverEndDate}>
                      Review &amp; Submit
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Bulk Date Amendment Modal */}
      {showBulkDateModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Bulk Amend Inception Dates</h2>
              <button className="close-btn" onClick={() => setShowBulkDateModal(false)}>✕</button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '20px', color: '#4a5568' }}>
                You are about to change the inception date for <strong>{selectedVehicles.size}</strong> vehicle(s).
              </p>

              <div className="form-group">
                <label>New Inception Date (Cover Start Date) *</label>
                <input
                  type="date"
                  value={bulkAmendDate}
                  onChange={(e) => setBulkAmendDate(e.target.value)}
                  style={{ width: '100%', padding: '10px', fontSize: '14px', borderRadius: '6px', border: '1px solid #cbd5e0' }}
                  required
                />
                <small style={{ display: 'block', marginTop: '8px', color: '#718096', fontSize: '12px' }}>
                  Current: {bulkAmendDate ? formatDateBritish(bulkAmendDate) : 'Not set'}
                </small>
              </div>

              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                backgroundColor: '#f7fafc', 
                borderRadius: '6px',
                border: '1px solid #e2e8f0'
              }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#2d3748' }}>Selected Vehicles:</h4>
                <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '13px', color: '#4a5568' }}>
                  {Array.from(selectedVehicles).map(vehicleId => {
                    const vehicle = vehicles.find(v => v.id === vehicleId);
                    return vehicle ? (
                      <div key={vehicleId} style={{ padding: '4px 0' }}>
                        • {vehicle.registration} - {vehicle.make} {vehicle.model}
                      </div>
                    ) : null;
                  })}
                </div>
              </div>

              <div className="form-actions" style={{ marginTop: '24px' }}>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowBulkDateModal(false);
                    setSelectedVehicles(new Set());
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={handleBulkDateAmendment}
                  disabled={bulkAmendSubmitting}
                >
                  {bulkAmendSubmitting ? 'Updating…' : `Update ${selectedVehicles.size} Vehicle(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleList;




